import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { collection, query, orderBy, onSnapshot, doc, updateDoc, arrayUnion, Timestamp, getDoc, setDoc, getDocs, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../../firebase.js";
import { useAuth } from "../../../useAuth.js";
// import { auth } from "../../../firebase.js";
import "./userDashboard.css";

// Alteration options for receipt lookup item selection (matches admin alterationDetails keys)
const RECEIPT_LOOKUP_ALTERATIONS = {
  Jacket: [
    { key: 'sleeveLonger', label: 'Sleeve Longer', valueKey: 'sleeveLongerValue' },
    { key: 'sleeveShorter', label: 'Sleeve Shorter', valueKey: 'sleeveShorterValue' },
    { key: 'takeIn', label: 'Take In', valueKey: 'takeInValue' },
    { key: 'takeOut', label: 'Take Out', valueKey: 'takeOutValue' },
    { key: 'shorterJacket', label: 'Shorter Jacket', valueKey: 'shorterJacketValue' },
    { key: 'shoulderPads', label: 'Shoulder Pads' },
    { key: 'functionButtonHole', label: 'Functional Button Hole' },
    { key: 'changeButtonsJacket', label: 'Change Buttons' },
    { key: 'taperSleeve', label: 'Taper Sleeve', valueKey: 'taperSleeveValue' },
    { key: 'shattnezTest', label: 'Shattnez Test' },
    { key: 'removeShattnez', label: 'Remove Shattnez' },
  ],
  Pants: [
    { key: 'hemming', label: 'Hemming', valueKey: 'hemmingValue' },
    { key: 'taperPants', label: 'Taper Pants', valueKey: 'taperPantsValue' },
    { key: 'waistIn', label: 'Waist In', valueKey: 'waistInValue' },
    { key: 'waistOut', label: 'Waist Out', valueKey: 'waistOutValue' },
    { key: 'seatIn', label: 'Seat In', valueKey: 'seatInValue' },
    { key: 'seatOut', label: 'Seat Out', valueKey: 'seatOutValue' },
    { key: 'crotchIn', label: 'Crotch In', valueKey: 'crotchInValue' },
    { key: 'crotchOut', label: 'Crotch Out', valueKey: 'crotchOutValue' },
    { key: 'crotchLining', label: 'Crotch Lining' },
    { key: 'changeZipper', label: 'Change Zipper' },
    { key: 'length', label: 'Length', valueKeys: ['lengthShorter', 'lengthLonger'] },
    { key: 'changeButtons', label: 'Change Buttons' },
    { key: 'suspenderButtons', label: 'Suspender Buttons' },
  ],
  Shirt: [
    { key: 'shorterSleeve', label: 'Shorter Sleeve', valueKey: 'shorterSleeveValue' },
    { key: 'takeInShirt', label: 'Take In', valueKey: 'takeInShirtValue' },
    { key: 'takeOutShirt', label: 'Take Out', valueKey: 'takeOutShirtValue' },
    { key: 'iron', label: 'Iron' },
  ],
  Vest: [
    { key: 'changeButtonsVest', label: 'Change Buttons' },
    { key: 'takeInVest', label: 'Take In', valueKey: 'takeInVestValue' },
    { key: 'takeOutVest', label: 'Take Out', valueKey: 'takeOutVestValue' },
  ],
};

// Flat map alteration key -> label for split-assignment display
const ALTERATION_KEY_LABELS = Object.values(RECEIPT_LOOKUP_ALTERATIONS).flat().reduce((acc, { key, label }) => {
  acc[key] = label;
  return acc;
}, {});

/** Return list of { key, label } for alteration types that are set on the item (from alterationDetails). */
function getAlterationKeysWithLabels(item) {
  if (!item?.alterationDetails) return [];
  const out = [];
  Object.keys(ALTERATION_KEY_LABELS).forEach((key) => {
    const v = item.alterationDetails[key];
    const isSet = v === true || (typeof v === 'string' && v.trim() !== '') || (typeof v === 'number' && !Number.isNaN(v));
    if (isSet) out.push({ key, label: ALTERATION_KEY_LABELS[key] });
  });
  return out;
}

/** Firestore `Customers` / `SMSOptOuts` document id: 10-digit US (no leading country 1). */
function normalizePhoneForCustomerDocId(phone) {
  if (phone == null || phone === '') return '';
  const d = String(phone).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return d.slice(1);
  if (d.length === 10) return d;
  return '';
}

function getOrderStatus(order) {
  return (order?.status || '').toLowerCase().trim();
}

function isReadyForPickUpStatus(order) {
  return getOrderStatus(order) === 'ready for pick up';
}

function playChimeWithContext(audioContext) {
  try {
    const frequencies = [523.25, 659.25, 783.99];
    const duration = 0.5;
    const gainNode = audioContext.createGain();
    gainNode.connect(audioContext.destination);
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
    frequencies.forEach((freq, index) => {
      const oscillator = audioContext.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
      const noteGain = audioContext.createGain();
      noteGain.gain.setValueAtTime(0.2, audioContext.currentTime);
      noteGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration + (index * 0.1));
      oscillator.connect(noteGain);
      noteGain.connect(gainNode);
      oscillator.start(audioContext.currentTime + (index * 0.05));
      oscillator.stop(audioContext.currentTime + duration + (index * 0.1));
    });
  } catch (e) {
    console.warn('Chime play failed:', e);
  }
}

function UserDashboard() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastOrderUrlParamRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [assignedOrders, setAssignedOrders] = useState([]);
  const [awaitingPickUpOrders, setAwaitingPickUpOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentView, setCurrentView] = useState('new'); // 'new', 'assigned', 'awaitingPickUp', or 'completed'
  const [slideDirection, setSlideDirection] = useState('right'); // 'left' or 'right'
  const [expandedCards, setExpandedCards] = useState({});
  const [sortByNew, setSortByNew] = useState('dueDate'); // Sorting for new orders (default: due date)
  const [sortByAssigned, setSortByAssigned] = useState('dueDate'); // Sorting for assigned orders (default: due date)
  const [sortByAwaitingPickUp, setSortByAwaitingPickUp] = useState('dueDate'); // Sorting for awaiting pick up orders (default: due date)
  const [sortOrderNew, setSortOrderNew] = useState('desc'); // 'asc' or 'desc' for new orders
  const [sortOrderAssigned, setSortOrderAssigned] = useState('desc'); // 'asc' or 'desc' for assigned orders
  const [sortOrderAwaitingPickUp, setSortOrderAwaitingPickUp] = useState('desc'); // 'asc' or 'desc' for awaiting pick up orders
  const [completedOrders, setCompletedOrders] = useState([]); // All completed (picked up) orders
  const [searchTermNew, setSearchTermNew] = useState(''); // Search term for new orders
  const [searchTermAssigned, setSearchTermAssigned] = useState(''); // Search term for assigned orders
  const [searchTermAwaitingPickUp, setSearchTermAwaitingPickUp] = useState(''); // Search term for awaiting pick up orders
  const [searchTermCompleted, setSearchTermCompleted] = useState(''); // Search term for completed alterations
  const [completedOrdersPage, setCompletedOrdersPage] = useState(0); // Current page for completed orders (10 per page)
  const [showMeasurements, setShowMeasurements] = useState(false); // Toggle customer measurements section
  const [customerMeasurements, setCustomerMeasurements] = useState({}); // Customer measurement data
  const [savingMeasurements, setSavingMeasurements] = useState(false); // Saving state for measurements
  const measurementsRef = useRef(null); // Ref for measurements section
  const previousOrderIdsRef = useRef(new Set()); // Track previous order IDs to detect new orders
  const isAdminRef = useRef(false);
  const audioContextRef = useRef(null); // Shared AudioContext, unlocked on user gesture (required for iOS/WebKit)
  const [soundUnlockedForSession, setSoundUnlockedForSession] = useState(false);
  const [showPushNotificationModal, setShowPushNotificationModal] = useState(false);
  const [pushNotificationSelectedIds, setPushNotificationSelectedIds] = useState(new Set());
  const [pushNotificationSearchTerm, setPushNotificationSearchTerm] = useState('');
  const [pushNotificationSending, setPushNotificationSending] = useState(false);
  const [markAsPickedUpSending, setMarkAsPickedUpSending] = useState(false);
  const [pushNotificationResult, setPushNotificationResult] = useState(null); // { sent, failed, errors, markedAsPickedUp }
  const [showPushNotificationCombinedConfirm, setShowPushNotificationCombinedConfirm] = useState(false);
  const [combinedPushActionSending, setCombinedPushActionSending] = useState(false);
  const [completedSelectedIds, setCompletedSelectedIds] = useState(new Set());
  const [completedSelectionMode, setCompletedSelectionMode] = useState(false);
  const [bulkStatusCompleted, setBulkStatusCompleted] = useState('order created');
  const [bulkStatusSending, setBulkStatusSending] = useState(false);
  const [receiptLookup, setReceiptLookup] = useState('');
  const [receiptLookupLoading, setReceiptLookupLoading] = useState(false);
  const [receiptLookupError, setReceiptLookupError] = useState('');
  const [showReceiptLookupModal, setShowReceiptLookupModal] = useState(false);
  const [receiptLookupOrder, setReceiptLookupOrder] = useState(null); // unassigned order found – show item selection
  const [receiptLookupLineItems, setReceiptLookupLineItems] = useState([]); // mutable line items for "needs alteration" selection
  const [expandedReceiptLookupItem, setExpandedReceiptLookupItem] = useState(null); // index of item whose alteration panel is open
  const [customerSmsByPhone, setCustomerSmsByPhone] = useState({}); // normalized phone -> { smsSubscribed, optedOut } from Customers + SMSOptOuts (not order fields)
  const [pushNotificationSmsLookupLoading, setPushNotificationSmsLookupLoading] = useState(false);
  const [splitModeItem, setSplitModeItem] = useState(null); // { orderId, itemIndex } when splitting alterations between tailors
  const [selectedAlterationKeysForSplit, setSelectedAlterationKeysForSplit] = useState(new Set());
  const [splitAssignTailorId, setSplitAssignTailorId] = useState('');
  const [splitAssignSaving, setSplitAssignSaving] = useState(false);

  // Play chime using shared AudioContext (must be unlocked via "Enable sound" on iOS/WebKit)
  const playChime = useCallback(() => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      let ctx = audioContextRef.current;
      if (!ctx) {
        ctx = new AudioContextClass();
        audioContextRef.current = ctx;
      }
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => playChimeWithContext(ctx)).catch(() => {});
        return;
      }
      playChimeWithContext(ctx);
    } catch (error) {
      console.warn('Could not play chime sound:', error);
    }
  }, []);

  // One-time unlock for iOS/WebKit: resume AudioContext on user gesture so chime can play later
  const unlockSoundForSession = () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      let ctx = audioContextRef.current;
      if (!ctx) {
        ctx = new AudioContextClass();
        audioContextRef.current = ctx;
      }
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => {
          playChimeWithContext(ctx);
          setSoundUnlockedForSession(true);
        }).catch(() => {});
      } else {
        playChimeWithContext(ctx);
        setSoundUnlockedForSession(true);
      }
    } catch (e) {
      console.warn('Could not unlock sound:', e);
    }
  };

  const getIsCompactView = () => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth <= 1310;
  };

  const [isCompactView, setIsCompactView] = useState(getIsCompactView);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      setIsCompactView(getIsCompactView());
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if current user is admin (ref kept in sync so snapshot callback always has current value)
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          // Force refresh token to get latest claims
          await user.getIdToken(true);
          const tokenResult = await user.getIdTokenResult();
          const admin = tokenResult.claims.admin === true;
          setIsAdmin(admin);
          isAdminRef.current = admin;
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
          isAdminRef.current = false;
        }
      } else {
        setIsAdmin(false);
        isAdminRef.current = false;
      }
    };

    checkAdminStatus();
  }, [user]);

  // Open receipt lookup modal when navigating from sidebar "Look up" (state.openReceiptLookup)
  useEffect(() => {
    if (location.state?.openReceiptLookup) {
      setShowReceiptLookupModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openReceiptLookup, location.pathname, navigate]);

  useEffect(() => {
    if (!user || authLoading) {
      return;
    }

    const ordersRef = collection(db, "Orders");
    
    let q;
    try {
      q = query(
        ordersRef,
        orderBy("timestamp", "desc")
      );
    } catch (error) {
      console.warn("⚠️ Error creating ordered query, falling back to unordered:", error);
      q = query(ordersRef);
    }

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      try {
        const ordersList = [];

        querySnapshot.forEach((doc) => {
          const orderData = doc.data();
          ordersList.push({
            id: doc.id,
            ...orderData
          });
        });

        // Sort orders by timestamp
        ordersList.sort((a, b) => {
          const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
          const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
          return timeB - timeA; 
        });

        // Filter orders that have at least one item needing alteration
        const ordersWithAlterations = ordersList.filter(order => {
          if (!order.line_items || !Array.isArray(order.line_items)) {
            return false;
          }

          return order.line_items.some(item => item.needsAlteration === true);
        });

        // Track which orders are "new" (newly appeared in the alteration set)
        const currentAlterationOrderIds = new Set(ordersWithAlterations.map(order => order.id));
        const previousOrderIds = previousOrderIdsRef.current;

        // Exclude orders that are marked as "picked up" from active alterations
        const activeOrders = ordersWithAlterations.filter(order => {
          return getOrderStatus(order) !== 'picked up';
        });

        // Awaiting pick up: all orders with this status, including those without a needsAlteration flag
        const awaitingPickUp = ordersList.filter(isReadyForPickUpStatus);

        // Separate into unassigned and assigned orders (excluding ready for pick up and picked up orders)
        const unassigned = activeOrders.filter(order => {
          const status = getOrderStatus(order);
          return (!order.assignedUser || order.assignedUser === '') &&
                 status !== 'ready for pick up' &&
                 status !== 'picked up';
        });

        // Detect new orders that appear in the New Orders section and play chime (only for non-admin users)
        if (previousOrderIds.size > 0) {
          const newOrderIds = [...currentAlterationOrderIds].filter(id => !previousOrderIds.has(id));
          if (newOrderIds.length > 0) {
            const unassignedIds = new Set(unassigned.map(o => o.id));
            const newOrdersInNewSection = newOrderIds.filter(id => unassignedIds.has(id));
            if (newOrdersInNewSection.length > 0 && !isAdminRef.current) {
              playChime();
            }
          }
        }
        previousOrderIdsRef.current = currentAlterationOrderIds;
        
        const assigned = activeOrders.filter(order => {
          const status = getOrderStatus(order);
          return order.assignedUser && order.assignedUser !== '' &&
                 status !== 'ready for pick up' &&
                 status !== 'picked up';
        });

        // Filter completed orders (picked up with alterations) - sorted by most recent first
        const completed = ordersWithAlterations.filter(order => {
          const status = (order.status || '').toLowerCase().trim();
          return status === 'picked up';
        }).sort((a, b) => {
          // Sort by timestamp descending (most recent first)
          const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
          const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
          return timeB - timeA;
        });

        console.log('Unassigned orders:', unassigned);
        console.log('Assigned orders:', assigned);
        console.log('Awaiting pick up orders:', awaitingPickUp);
        console.log('Completed orders:', completed.length);

        setOrders(activeOrders);
        setUnassignedOrders(unassigned);
        setAssignedOrders(assigned);
        setAwaitingPickUpOrders(awaitingPickUp);
        setCompletedOrders(completed);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error("Error processing orders:", err);
        setError("Failed to process orders. Please refresh the page.");
        setLoading(false);
      }
    }, (err) => {
      console.error("Error listening to orders:", err);
      setError("Failed to connect to orders. Please check your connection.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, authLoading, playChime]);

  // Keep open order modal in sync with Firestore (e.g. Shopify webhook phone/name updates)
  useEffect(() => {
    if (!showModal) return;
    setSelectedOrder((prev) => {
      if (!prev?.id) return prev;
      const live = orders.find((o) => o.id === prev.id);
      if (!live) return prev;

      const hasPendingItemEdits = Object.keys(pendingChanges).some((key) => {
        const c = pendingChanges[key];
        return c?.orderId === prev.id && c?.itemIndex != null;
      });

      const patch = {};
      if ((prev.phone || '') !== (live.phone || '') && !pendingChanges[`${prev.id}_order_phone`]) {
        patch.phone = live.phone || '';
      }
      if ((prev.name || '') !== (live.name || '') && !pendingChanges[`${prev.id}_order_name`]) {
        patch.name = live.name || '';
      }
      if ((prev.receiptNumber || '') !== (live.receiptNumber || '')) {
        patch.receiptNumber = live.receiptNumber || '';
      }
      if (!hasPendingItemEdits) {
        const sig = (items) =>
          (Array.isArray(items) ? items : [])
            .map((i) => `${i?.shopifyLineItemId || ''}|${i?.name || ''}|${i?.quantity ?? ''}`)
            .join(';');
        if (sig(prev.line_items) !== sig(live.line_items)) {
          patch.line_items = live.line_items || [];
        }
      }

      if (Object.keys(patch).length === 0) return prev;
      return { ...prev, ...patch };
    });
  }, [orders, showModal, pendingChanges]);

  // Fetch users for assignment dropdown
  useEffect(() => {
    if (!user || authLoading) {
      return;
    }

    const usersRef = collection(db, "Users");
    const unsubscribe = onSnapshot(usersRef, (querySnapshot) => {
      try {
        const usersList = [];
        querySnapshot.forEach((doc) => {
          const userData = doc.data();
          usersList.push({
            id: doc.id,
            ...userData
          });
        });
        setUsers(usersList);
      } catch (err) {
        console.error("Error processing users:", err);
      }
    }, (err) => {
      console.error("Error listening to users:", err);
    });

    return () => unsubscribe();
  }, [user, authLoading]);

  // When push notification modal is open, load SMS opt-in from Customer profile + SMSOptOuts (order.smsSubscribed is not used)
  useEffect(() => {
    if (!showPushNotificationModal || !awaitingPickUpOrders?.length) {
      setCustomerSmsByPhone({});
      setPushNotificationSmsLookupLoading(false);
      return;
    }
    const phones = [...new Set(awaitingPickUpOrders.map((o) => normalizePhoneForCustomerDocId(o.phone)).filter(Boolean))];
    if (phones.length === 0) {
      setCustomerSmsByPhone({});
      setPushNotificationSmsLookupLoading(false);
      return;
    }
    setPushNotificationSmsLookupLoading(true);
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(
        phones.map(async (normalizedPhone) => {
          const [customerSnap, optOutSnap] = await Promise.all([
            getDoc(doc(db, 'Customers', normalizedPhone)),
            getDoc(doc(db, 'SMSOptOuts', normalizedPhone))
          ]);
          const smsSubscribed = customerSnap.exists() && customerSnap.data()?.smsSubscribed === true;
          const optedOut = optOutSnap.exists() && optOutSnap.data()?.optedOut === true;
          map[normalizedPhone] = { smsSubscribed, optedOut };
        })
      );
      if (!cancelled) {
        setCustomerSmsByPhone(map);
        setPushNotificationSmsLookupLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showPushNotificationModal, awaitingPickUpOrders]);

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';

    const cleaned = phoneNumber.replace(/\D/g, '');

    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 7) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    } else {
      return cleaned;
    }
  };

  const formatDateOnly = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      // Handle date strings like "2025-01-15" or ISO strings
      // For YYYY-MM-DD format, parse manually to avoid timezone issues
      const dateMatch = timestamp.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        // Return formatted directly without creating Date object to avoid timezone shift
        return `${month}/${day}/${year}`;
      }
      date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      return 'N/A';
    }
    
    // Check for invalid date
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/New_York'
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
    }

    const localTime = date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/New_York'
    });
    
    return localTime;
  };

  // Helper function to get dueDate from order or first item's alterationDetails
  const getOrderDueDate = (order) => {
    // Check order-level dueDate first
    if (order.dueDate) return order.dueDate;
    
    // Check line_items for alterationDetails.dueDate
    // Check alterationDetails regardless of needsAlteration flag (data might exist even if flag not set)
    if (order.line_items && Array.isArray(order.line_items)) {
      for (const item of order.line_items) {
        if (item.alterationDetails && item.alterationDetails.dueDate) {
          return item.alterationDetails.dueDate;
        }
      }
    }
    
    return null;
  };

  // Helper function to get dueTime from order or first item's alterationDetails
  const getOrderDueTime = (order) => {
    // Check order-level dueTime first
    if (order.dueTime) return order.dueTime;
    
    // Check line_items for alterationDetails.dueTime
    // Check alterationDetails regardless of needsAlteration flag (data might exist even if flag not set)
    if (order.line_items && Array.isArray(order.line_items)) {
      for (const item of order.line_items) {
        if (item.alterationDetails && item.alterationDetails.dueTime) {
          return item.alterationDetails.dueTime;
        }
      }
    }
    
    return null;
  };

  // Helper function to get priority from order or first item's alterationDetails
  const getOrderPriority = (order) => {
    // Check order-level priority first
    if (order.priority) return order.priority;
    
    // Check line_items for alterationDetails.priority
    // Check alterationDetails regardless of needsAlteration flag (data might exist even if flag not set)
    if (order.line_items && Array.isArray(order.line_items)) {
      for (const item of order.line_items) {
        if (item.alterationDetails && item.alterationDetails.priority) {
          return item.alterationDetails.priority;
        }
      }
    }
    
    return 'none';
  };

  const formatTime12Hour = (time24) => {
    if (!time24) return 'Not Set';
    // If it's a Firestore Timestamp or Date, format to H:MM AM/PM
    if (typeof time24 === 'object') {
      const d = time24.toDate ? time24.toDate() : (time24 instanceof Date ? time24 : null);
      if (d) {
        const hours = d.getHours();
        const minutes = `${d.getMinutes()}`.padStart(2, '0');
        const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const ampm = hours >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      }
    }
    // Normalize strings/numbers like "930", 930, "09:30", "9:30"
    const raw = String(time24).trim();
    const colonIdx = raw.indexOf(':');
    let hh = '';
    let mm = '';
    if (colonIdx !== -1) {
      [hh, mm] = raw.split(':');
    } else {
      const digits = raw.replace(/\D/g, '');
      if (digits.length === 4) {
        hh = digits.slice(0, 2);
        mm = digits.slice(2);
      } else if (digits.length === 3) {
        hh = digits.slice(0, 1);
        mm = digits.slice(1);
      } else {
        return 'Not Set';
      }
    }
    const hour24 = parseInt(hh, 10);
    if (Number.isNaN(hour24)) return 'Not Set';
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    return `${hour12}:${`${mm}`.padStart(2, '0')} ${ampm}`;
  };

  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
    // Load customer measurements
    if (order.phone) {
      loadCustomerMeasurements(order.phone);
    }
  };

  const closeOrderModal = () => {
    setSelectedOrder(null);
    setShowModal(false);
    setShowMeasurements(false);
    setCustomerMeasurements({});
  };

  const openOrderModalRef = useRef(openOrderModal);
  openOrderModalRef.current = openOrderModal;

  useEffect(() => {
    const orderId = searchParams.get("order");
    if (!orderId) {
      lastOrderUrlParamRef.current = null;
      return;
    }
    if (!user || authLoading || loading) return;
    if (lastOrderUrlParamRef.current === orderId) return;

    lastOrderUrlParamRef.current = orderId;
    let cancelled = false;

    const clearOrderParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("order");
          return next;
        },
        { replace: true }
      );
    };

    (async () => {
      try {
        let order =
          orders.find((o) => o.id === orderId) ||
          completedOrders.find((o) => o.id === orderId);
        if (!order) {
          try {
            const snap = await getDoc(doc(db, "Orders", orderId));
            if (snap.exists()) {
              order = { id: snap.id, ...snap.data() };
            }
          } catch (e) {
            console.warn("Could not load order from link:", e);
          }
        }
        if (cancelled) return;
        if (!order) {
          clearOrderParam();
          return;
        }
        openOrderModalRef.current(order);
        clearOrderParam();
      } finally {
        if (!cancelled) {
          lastOrderUrlParamRef.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
      lastOrderUrlParamRef.current = null;
    };
  }, [searchParams, user, authLoading, loading, orders, completedOrders, setSearchParams]);

  const goToAdminOrderView = () => {
    if (!selectedOrder?.id) return;
    navigate(`/admin?order=${encodeURIComponent(selectedOrder.id)}`);
  };

  const handleReceiptLookup = async () => {
    const trimmed = (receiptLookup || '').trim();
    if (!trimmed) {
      setReceiptLookupError('Enter a receipt number.');
      return;
    }
    setReceiptLookupError('');
    setReceiptLookupLoading(true);
    try {
      const ordersRef = collection(db, 'Orders');
      const q = query(ordersRef, where('receiptNumber', '==', trimmed));
      const snap = await getDocs(q);
      if (snap.empty) {
        setReceiptLookupError('No order found with that receipt number.');
        setReceiptLookupLoading(false);
        return;
      }
      const orderDoc = snap.docs[0];
      const order = { id: orderDoc.id, ...orderDoc.data() };
      setReceiptLookup('');
      setReceiptLookupError('');
      // Only show item selection for unassigned orders (look up only; not admin-assigned)
      if (order.assignedUser) {
        setShowReceiptLookupModal(false);
        setReceiptLookupOrder(null);
        setReceiptLookupLineItems([]);
        openOrderModal(order);
      } else {
        const items = order.line_items && Array.isArray(order.line_items)
          ? order.line_items.map(item => ({ ...item, needsAlteration: !!item.needsAlteration }))
          : [];
        setReceiptLookupOrder(order);
        setReceiptLookupLineItems(items);
      }
    } catch (err) {
      console.error('Receipt lookup error:', err);
      setReceiptLookupError(err.message || 'Lookup failed.');
    } finally {
      setReceiptLookupLoading(false);
    }
  };

  const toggleReceiptLookupItemNeedsAlteration = (index) => {
    setReceiptLookupLineItems(prev =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              needsAlteration: !item.needsAlteration,
              alterationDetails: item.needsAlteration ? {} : (item.alterationDetails || {}),
            }
          : item
      )
    );
    if (expandedReceiptLookupItem === index) setExpandedReceiptLookupItem(null);
  };

  const updateReceiptLookupAlteration = (itemIndex, field, value) => {
    setReceiptLookupLineItems(prev =>
      prev.map((item, i) => {
        if (i !== itemIndex) return item;
        const details = { ...(item.alterationDetails || {}), [field]: value };
        return { ...item, alterationDetails: details };
      })
    );
  };

  const handleReceiptLookupContinue = async () => {
    if (!receiptLookupOrder) return;
    const orderRef = doc(db, 'Orders', receiptLookupOrder.id);
    setReceiptLookupLoading(true);
    try {
      await updateDoc(orderRef, { line_items: receiptLookupLineItems });
      const updatedOrder = { ...receiptLookupOrder, line_items: receiptLookupLineItems };
      setReceiptLookupOrder(null);
      setReceiptLookupLineItems([]);
      setShowReceiptLookupModal(false);
      openOrderModal(updatedOrder);
    } catch (err) {
      console.error('Failed to save alteration selection:', err);
      setReceiptLookupError(err.message || 'Failed to save.');
    } finally {
      setReceiptLookupLoading(false);
    }
  };

  const closeReceiptLookupModal = () => {
    setShowReceiptLookupModal(false);
    setReceiptLookupOrder(null);
    setReceiptLookupLineItems([]);
    setExpandedReceiptLookupItem(null);
    setReceiptLookupError('');
  };

  const toggleCompletedSelection = (orderId) => {
    setCompletedSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const selectAllCompletedOnPage = () => {
    setCompletedSelectedIds(prev => {
      const next = new Set(prev);
      displayedCompletedOrders.forEach(o => next.add(o.id));
      return next;
    });
  };

  const clearCompletedSelection = () => {
    setCompletedSelectedIds(new Set());
  };

  const applyBulkStatusCompleted = async () => {
    if (completedSelectedIds.size === 0) return;
    const status = bulkStatusCompleted.trim();
    if (!status) return;
    setBulkStatusSending(true);
    const statusLogEntry = {
      userId: user?.uid || 'unknown',
      userName: user?.displayName || user?.email || 'Unknown User',
      status,
      timestamp: Timestamp.now()
    };
    try {
      for (const orderId of completedSelectedIds) {
        const orderRef = doc(db, 'Orders', orderId);
        await updateDoc(orderRef, {
          status,
          statusLog: arrayUnion(statusLogEntry)
        });
      }
      setCompletedSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk status update error:', err);
    } finally {
      setBulkStatusSending(false);
    }
  };

  // Load customer measurements when modal opens
  const loadCustomerMeasurements = async (phone) => {
    if (!phone) return;
    
    try {
      const customerRef = doc(db, "Customers", phone);
      const customerDoc = await getDoc(customerRef);
      
      if (customerDoc.exists()) {
        const customerData = customerDoc.data();
        setCustomerMeasurements(customerData.alterations || {});
      }
    } catch (error) {
      console.error("Error loading customer measurements:", error);
    }
  };

  // Update customer measurement field
  const updateCustomerMeasurement = (field, value) => {
    setCustomerMeasurements(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Toggle measurements and scroll to section
  const toggleMeasurements = () => {
    const newShowState = !showMeasurements;
    setShowMeasurements(newShowState);
    
    // If opening, scroll to measurements section after a brief delay
    if (newShowState) {
      setTimeout(() => {
        measurementsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  };

  // Save customer measurements
  const saveCustomerMeasurements = async () => {
    if (!selectedOrder || !selectedOrder.phone) {
      alert("No customer phone number found");
      return;
    }

    setSavingMeasurements(true);

    try {
      const customerRef = doc(db, "Customers", selectedOrder.phone);
      await setDoc(customerRef, {
        alterations: customerMeasurements
      }, { merge: true });

      alert("Measurements saved successfully!");
    } catch (error) {
      console.error("Error saving measurements:", error);
      alert("Failed to save measurements. Please try again.");
    } finally {
      setSavingMeasurements(false);
    }
  };

  const getAlterationItemCount = (order) => {
    if (!order.line_items || !Array.isArray(order.line_items)) {
      return 0;
    }
    return order.line_items.filter(item => item.needsAlteration === true).length;
  };

  const toggleCardExpansion = (orderId, event = null) => {
    if (event) {
      event.stopPropagation();
    }

    setExpandedCards(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const updateAlterationDetail = (field, value) => {
    // Handle order-level fields (assignedUser, status, priority)
    if (field === 'assignedUser' || field === 'status' || field === 'priority') {
      // Update selectedOrder
      setSelectedOrder(prev => ({
        ...prev,
        [field]: value
      }));

      // Get the original value from the database
      const originalOrder = orders.find(order => order.id === selectedOrder.id);
      const originalValue = originalOrder?.[field] || '';
      
      // Handle pending changes
      const trimmedValue = value.trim();
      const trimmedOriginalValue = originalValue.toString().trim();
      
      // Check if the value is actually different from the original
      const isDifferent = trimmedValue !== trimmedOriginalValue;
      
      // Special handling for default values - these should not be considered changes (status "order created" is allowed so admin can revert)
      const isDefaultValue = (field === 'assignedUser' && trimmedValue === '') || 
                            (field === 'priority' && trimmedValue === 'none');
      
      if (!isDifferent || isDefaultValue) {
        // Remove from pending changes when value matches original or is default
        setPendingChanges(prev => {
          const newPending = { ...prev };
          delete newPending[`${selectedOrder.id}_order_${field}`];
          return newPending;
        });
      } else {
        // Add to pending changes when value is different from original
        setPendingChanges(prev => ({
          ...prev,
          [`${selectedOrder.id}_order_${field}`]: {
            orderId: selectedOrder.id,
            itemIndex: 'order', // Special marker for order-level fields
            field: field, // Direct field name for order level
            value: trimmedValue
          }
        }));
      }
    }
  };

  const openSplitMode = (orderId, itemIndex) => {
    const current = splitModeItem;
    if (current && current.orderId === orderId && current.itemIndex === itemIndex) {
      setSplitModeItem(null);
      setSelectedAlterationKeysForSplit(new Set());
      setSplitAssignTailorId('');
    } else {
      setSplitModeItem({ orderId, itemIndex });
      setSelectedAlterationKeysForSplit(new Set());
      setSplitAssignTailorId('');
    }
  };

  const isSplitModeFor = (orderId, itemIndex) =>
    splitModeItem && splitModeItem.orderId === orderId && splitModeItem.itemIndex === itemIndex;

  const toggleSplitAlterationKey = (key) => {
    setSelectedAlterationKeysForSplit((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const assignSelectedToTailor = async () => {
    if (!splitModeItem || !selectedOrder || selectedOrder.id !== splitModeItem.orderId) return;
    const tailorId = splitAssignTailorId?.trim();
    if (!tailorId) return;
    const keys = Array.from(selectedAlterationKeysForSplit);
    if (keys.length === 0) return;
    const itemIndex = splitModeItem.itemIndex;
    const lineItems = [...(selectedOrder.line_items || [])];
    if (itemIndex < 0 || itemIndex >= lineItems.length) return;
    const item = lineItems[itemIndex];
    const assignments = { ...(item.alterationAssignments || {}) };
    keys.forEach((k) => { assignments[k] = tailorId; });
    lineItems[itemIndex] = { ...item, alterationAssignments: assignments };
    setSplitAssignSaving(true);
    try {
      const orderRef = doc(db, 'Orders', selectedOrder.id);
      await updateDoc(orderRef, { line_items: lineItems });
      setSelectedOrder((prev) => (prev && prev.id === selectedOrder.id ? { ...prev, line_items: lineItems } : prev));
      setOrders((prev) => prev.map((o) => (o.id === selectedOrder.id ? { ...o, line_items: lineItems } : o)));
      setSelectedAlterationKeysForSplit(new Set());
    } finally {
      setSplitAssignSaving(false);
    }
  };

  const markAllAlterationsFinishedForItem = async (orderId, itemIndex) => {
    if (!selectedOrder || selectedOrder.id !== orderId) return;
    const lineItems = [...(selectedOrder.line_items || [])];
    if (itemIndex < 0 || itemIndex >= lineItems.length) return;
    const item = lineItems[itemIndex];
    const keys = Object.keys(item.alterationAssignments || {});
    if (keys.length === 0) return;
    const now = Timestamp.now();
    const finished = { ...(item.alterationFinished || {}) };
    keys.forEach((key) => { finished[key] = { finishedAt: now }; });
    lineItems[itemIndex] = { ...item, alterationFinished: finished };
    try {
      const orderRef = doc(db, 'Orders', orderId);
      await updateDoc(orderRef, { line_items: lineItems });
      setSelectedOrder((prev) => (prev && prev.id === orderId ? { ...prev, line_items: lineItems } : prev));
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, line_items: lineItems } : o)));
    } catch (err) {
      console.error('Error marking alterations finished:', err);
    }
  };

  const unassignAlteration = async (orderId, itemIndex, alterationKey) => {
    if (!selectedOrder || selectedOrder.id !== orderId) return;
    const lineItems = [...(selectedOrder.line_items || [])];
    if (itemIndex < 0 || itemIndex >= lineItems.length) return;
    const item = lineItems[itemIndex];
    const assignments = { ...(item.alterationAssignments || {}) };
    delete assignments[alterationKey];
    const finished = { ...(item.alterationFinished || {}) };
    delete finished[alterationKey];
    lineItems[itemIndex] = { ...item, alterationAssignments: assignments, alterationFinished: finished };
    try {
      const orderRef = doc(db, 'Orders', orderId);
      await updateDoc(orderRef, { line_items: lineItems });
      setSelectedOrder((prev) => (prev && prev.id === orderId ? { ...prev, line_items: lineItems } : prev));
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, line_items: lineItems } : o)));
    } catch (err) {
      console.error('Error unassigning alteration:', err);
    }
  };

  const saveAlterationChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      return;
    }

    const changeCount = Object.keys(pendingChanges).length;
    setSaving(true);
    try {
      // Group changes by order ID
      const changesByOrder = {};
      const orderLevelChanges = {};

      Object.values(pendingChanges).forEach(change => {
        if (!changesByOrder[change.orderId]) {
          changesByOrder[change.orderId] = [];
        }

        if (change.itemIndex === 'order') {
          if (!orderLevelChanges[change.orderId]) {
            orderLevelChanges[change.orderId] = {};
          }
          orderLevelChanges[change.orderId][change.field] = change.value;
        } else {
          // Item-level changes (not used in user dashboard but keeping for consistency)
          changesByOrder[change.orderId].push(change);
        }
      });

      const orderIds = new Set([
        ...Object.keys(changesByOrder),
        ...Object.keys(orderLevelChanges)
      ]);

      const statusLogStateEntries = {};

      for (const orderId of orderIds) {
        const orderRef = doc(db, "Orders", orderId);
        const orderFields = orderLevelChanges[orderId] || {};
        const itemLevelChanges = changesByOrder[orderId] || [];
        const updateData = { ...orderFields };

        // Apply item-level changes if any
        if (itemLevelChanges.length > 0) {
          const order = orders.find(o => o.id === orderId);
          if (order && order.line_items) {
            const updatedLineItems = [...order.line_items];
            
            itemLevelChanges.forEach(change => {
              const itemIndex = change.itemIndex;
              if (itemIndex !== null && updatedLineItems[itemIndex]) {
                if (!updatedLineItems[itemIndex].alterationDetails) {
                  updatedLineItems[itemIndex].alterationDetails = {};
                }
                
                const fieldName = change.field.replace('alterationDetails.', '');
                updatedLineItems[itemIndex].alterationDetails[fieldName] = change.value;
              }
            });
            
            updateData.line_items = updatedLineItems;
          }
        }

        let statusLogEntryForDb = null;
        let statusLogEntryForState = null;
        if (orderFields.status !== undefined) {
          statusLogEntryForDb = {
            userId: user?.uid || 'unknown',
            userName: user?.displayName || user?.email || 'Unknown User',
            status: orderFields.status,
            timestamp: Timestamp.now()
          };
          statusLogEntryForState = {
            userId: user?.uid || 'unknown',
            userName: user?.displayName || user?.email || 'Unknown User',
            status: orderFields.status,
            timestamp: new Date().toISOString()
          };
          statusLogStateEntries[orderId] = statusLogEntryForState;
        }

        const updatePayload = { ...updateData };
        if (statusLogEntryForDb) {
          updatePayload.statusLog = arrayUnion(statusLogEntryForDb);
        }

        await updateDoc(orderRef, updatePayload);

        // Handle bidirectional assignment - update user's assigned orders
        if (orderFields.assignedUser !== undefined) {
          // First, remove from all users (in case of reassignment)
          for (const usr of users) {
            if (usr.assignedOrders && usr.assignedOrders.includes(orderId)) {
              const userRef = doc(db, "Users", usr.id);
              const userUpdateData = {
                assignedOrders: usr.assignedOrders.filter(id => id !== orderId)
              };
              await updateDoc(userRef, userUpdateData);
            }
          }

          // Then, add to the new assigned user (if not empty)
          if (orderFields.assignedUser && orderFields.assignedUser !== '') {
            const assignedUser = users.find(u => u.name === orderFields.assignedUser);
            if (assignedUser) {
              const userRef = doc(db, "Users", assignedUser.id);
              const currentAssignedOrders = assignedUser.assignedOrders || [];
              
              // Only add if not already assigned
              if (!currentAssignedOrders.includes(orderId)) {
                const userUpdateData = {
                  assignedOrders: [...currentAssignedOrders, orderId]
                };
                await updateDoc(userRef, userUpdateData);
              }
            }
          }
        }
      }

      // Update local state
      setOrders(prev => prev.map(order => {
        const itemChanges = changesByOrder[order.id] || [];
        const orderFields = orderLevelChanges[order.id];
        const hasOrderFields = Boolean(orderFields);
        const hasItemChanges = itemChanges.length > 0;
        const statusLogEntry = statusLogStateEntries[order.id];

        if (!hasOrderFields && !hasItemChanges && !statusLogEntry) {
          return order;
        }

        const updatedOrder = { ...order };

        if (orderFields) {
          Object.assign(updatedOrder, orderFields);
        }

        if (hasItemChanges) {
          const updatedLineItems = [...order.line_items];
          itemChanges.forEach(change => {
            const itemIndex = change.itemIndex;
            if (itemIndex !== null && updatedLineItems[itemIndex]) {
              if (!updatedLineItems[itemIndex].alterationDetails) {
                updatedLineItems[itemIndex].alterationDetails = {};
              }
              const fieldName = change.field.replace('alterationDetails.', '');
              updatedLineItems[itemIndex].alterationDetails[fieldName] = change.value;
            }
          });
          updatedOrder.line_items = updatedLineItems;
        }

        if (statusLogEntry) {
          const existingLog = Array.isArray(order.statusLog) ? order.statusLog : [];
          updatedOrder.statusLog = [...existingLog, statusLogEntry];
        }
        
        return updatedOrder;
      }));

      // Update selectedOrder if it's the one being modified
      if (selectedOrder && orderIds.has(selectedOrder.id)) {
        setSelectedOrder(prev => {
          if (!prev) return prev;
          const orderFields = orderLevelChanges[prev.id];
          const statusLogEntry = statusLogStateEntries[prev.id];
          const itemChanges = changesByOrder[prev.id] || [];
          const hasOrderFields = Boolean(orderFields);
          const hasItemChanges = itemChanges.length > 0;
          const needsUpdate = hasOrderFields || hasItemChanges || statusLogEntry;

          if (!needsUpdate) {
            return prev;
          }

          let updatedOrder = { ...prev };
          if (orderFields) {
            Object.assign(updatedOrder, orderFields);
          }

          if (hasItemChanges && prev.line_items) {
            const updatedLineItems = [...prev.line_items];
            itemChanges.forEach(change => {
              const itemIndex = change.itemIndex;
              if (itemIndex !== null && updatedLineItems[itemIndex]) {
                if (!updatedLineItems[itemIndex].alterationDetails) {
                  updatedLineItems[itemIndex].alterationDetails = {};
                }
                const fieldName = change.field.replace('alterationDetails.', '');
                updatedLineItems[itemIndex].alterationDetails[fieldName] = change.value;
              }
            });
            updatedOrder.line_items = updatedLineItems;
          }

          if (statusLogEntry) {
            const existingLog = Array.isArray(prev.statusLog) ? prev.statusLog : [];
            updatedOrder.statusLog = [...existingLog, statusLogEntry];
          }

          return updatedOrder;
        });
      }

      // Clear pending changes before showing success message
      setPendingChanges({});
      
      // Show success confirmation
      const savedCount = changeCount;
      alert(`✅ Successfully saved ${savedCount} change${savedCount !== 1 ? 's' : ''}!`);
    } catch (error) {
      console.error("Error saving alteration changes:", error);
      alert("❌ Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Sorting functions
  const sortOrders = (orders, sortBy, sortOrder) => {
    const sorted = [...orders];
    
    sorted.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'dueDate': {
          const toMs = (val) => {
            if (!val) return Number.MAX_SAFE_INTEGER;
            if (val.toDate) return val.toDate().getTime();
            const d = val instanceof Date ? val : new Date(val);
            const t = d.getTime();
            return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
          };
          const dateA = toMs(getOrderDueDate(a));
          const dateB = toMs(getOrderDueDate(b));
          // For due date, "descending" means closest dates first (earliest first)
          // "ascending" means furthest dates first (latest first)
          comparison = dateA - dateB;
          break;
        }
          
        case 'priority':
          const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1, 'none': 0 };
          const priorityA = priorityOrder[a.priority] || 0;
          const priorityB = priorityOrder[b.priority] || 0;
          comparison = priorityA - priorityB;
          break;
          
        case 'dateCreated':
          const timeA = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : new Date(a.timestamp || 0).getTime();
          const timeB = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : new Date(b.timestamp || 0).getTime();
          comparison = timeA - timeB;
          break;
          
        case 'customerName':
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
          
        case 'assignedUser':
          const userA = (a.assignedUser || 'Unassigned').toLowerCase();
          const userB = (b.assignedUser || 'Unassigned').toLowerCase();
          comparison = userA.localeCompare(userB);
          break;
          
        case 'status':
          const statusA = (a.status || 'order created').toLowerCase();
          const statusB = (b.status || 'order created').toLowerCase();
          comparison = statusA.localeCompare(statusB);
          break;
          
        default:
          return 0;
      }
      
      // For dueDate, "descending" means earliest first (closest dates), so don't negate
      // For other fields, standard ascending/descending logic applies
      if (sortBy === 'dueDate') {
        return sortOrder === 'desc' ? comparison : -comparison;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  };

  // Filter orders by search term (order ID, barcode ID, receipt number, or customer name)
  const filterOrdersBySearch = (orders, searchTerm) => {
    if (!searchTerm.trim()) {
      return orders;
    }
    const searchLower = searchTerm.toLowerCase().trim().replace(/^r:\s*/i, '');
    return orders.filter(order =>
      order.id.toLowerCase().includes(searchLower) ||
      (order.barcodeId && String(order.barcodeId).toLowerCase().includes(searchLower)) ||
      (order.receiptNumber && order.receiptNumber.toLowerCase().includes(searchLower)) ||
      (order.name && order.name.toLowerCase().includes(searchLower))
    );
  };

  // Mark selected orders as picked up (from Push Notification modal)
  const markSelectedOrdersAsPickedUp = async () => {
    if (pushNotificationSelectedIds.size === 0) return;
    setMarkAsPickedUpSending(true);
    setPushNotificationResult(null);
    const statusLogEntry = {
      userId: user?.uid || 'unknown',
      userName: user?.displayName || user?.email || 'Unknown User',
      status: 'picked up',
      timestamp: Timestamp.now()
    };
    try {
      for (const orderId of pushNotificationSelectedIds) {
        const orderRef = doc(db, 'Orders', orderId);
        await updateDoc(orderRef, {
          status: 'picked up',
          statusLog: arrayUnion(statusLogEntry)
        });
      }
      setPushNotificationResult({ sent: 0, failed: 0, markedAsPickedUp: pushNotificationSelectedIds.size });
      setPushNotificationSelectedIds(new Set());
    } catch (err) {
      setPushNotificationResult({ sent: 0, failed: pushNotificationSelectedIds.size, errors: [err.message || String(err)], markedAsPickedUp: false });
    } finally {
      setMarkAsPickedUpSending(false);
    }
  };

  const formatPushNotificationOrderLabel = (order) => {
    const parts = [`Order ${order.id}`];
    if (order.receiptNumber) parts.push(`Receipt ${order.receiptNumber}`);
    if (order.name) parts.push(order.name);
    return parts.join(' · ');
  };

  const markOrdersAsPickedUpByIds = async (orderIds) => {
    if (!orderIds.length) return 0;
    const statusLogEntry = {
      userId: user?.uid || 'unknown',
      userName: user?.displayName || user?.email || 'Unknown User',
      status: 'picked up',
      timestamp: Timestamp.now()
    };
    for (const orderId of orderIds) {
      const orderRef = doc(db, 'Orders', orderId);
      await updateDoc(orderRef, {
        status: 'picked up',
        statusLog: arrayUnion(statusLogEntry)
      });
    }
    return orderIds.length;
  };

  const runCombinedPushAndPickup = async (notifyIds, pickupIds) => {
    setCombinedPushActionSending(true);
    setPushNotificationResult(null);
    let sent = 0;
    let failed = 0;
    let errors = [];
    let markedAsPickedUp = 0;
    try {
      if (notifyIds.length > 0) {
        const resend = httpsCallable(functions, 'resendReadyForPickUpNotification2');
        const { data } = await resend({ orderIds: notifyIds });
        sent = data?.sent || 0;
        failed = data?.failed || 0;
        errors = Array.isArray(data?.errors) ? data.errors : [];
      }
      if (pickupIds.length > 0) {
        markedAsPickedUp = await markOrdersAsPickedUpByIds(pickupIds);
      }
      setPushNotificationResult({ sent, failed, errors, markedAsPickedUp });
      setPushNotificationSelectedIds(new Set());
      setShowPushNotificationCombinedConfirm(false);
    } catch (err) {
      setPushNotificationResult({
        sent,
        failed: failed || notifyIds.length,
        errors: [...errors, err.message || String(err)],
        markedAsPickedUp
      });
    } finally {
      setCombinedPushActionSending(false);
    }
  };

  // Get filtered and sorted orders
  const filteredUnassignedOrders = filterOrdersBySearch(unassignedOrders, searchTermNew);
  const filteredAssignedOrders = filterOrdersBySearch(assignedOrders, searchTermAssigned);
  const filteredAwaitingPickUpOrders = filterOrdersBySearch(awaitingPickUpOrders, searchTermAwaitingPickUp);
  const filteredCompletedOrders = filterOrdersBySearch(completedOrders, searchTermCompleted);

  const sortedUnassignedOrders = sortOrders(filteredUnassignedOrders, sortByNew, sortOrderNew);
  const sortedAssignedOrders = sortOrders(filteredAssignedOrders, sortByAssigned, sortOrderAssigned);
  const sortedAwaitingPickUpOrders = sortOrders(filteredAwaitingPickUpOrders, sortByAwaitingPickUp, sortOrderAwaitingPickUp);

  const visiblePushNotificationOrders = sortedAwaitingPickUpOrders.filter((order) => {
    if (!pushNotificationSearchTerm.trim()) return true;
    const s = pushNotificationSearchTerm.toLowerCase().trim().replace(/^r:\s*/i, '');
    return (
      (order.id && order.id.toLowerCase().includes(s)) ||
      (order.barcodeId && String(order.barcodeId).toLowerCase().includes(s)) ||
      (order.receiptNumber && order.receiptNumber.toLowerCase().includes(s)) ||
      (order.name && order.name.toLowerCase().includes(s))
    );
  });
  const combinedNotifyOrders = visiblePushNotificationOrders.filter((o) => pushNotificationSelectedIds.has(o.id));
  const combinedPickupOrders = visiblePushNotificationOrders.filter((o) => !pushNotificationSelectedIds.has(o.id));
  const pushModalBusy = pushNotificationSending || markAsPickedUpSending || combinedPushActionSending;

  // Paginated completed orders (10 per page) - pagination applies to filtered list
  const completedOrdersStart = completedOrdersPage * 10;
  const completedOrdersEnd = completedOrdersStart + 10;
  const displayedCompletedOrders = filteredCompletedOrders.slice(completedOrdersStart, completedOrdersEnd);
  const hasMoreCompletedOrders = completedOrdersEnd < filteredCompletedOrders.length;

  const renderOrderCard = (order, options = {}) => {
    const openOnClick = options.openOnClick !== false;
    // Use helper functions to get dueDate and dueTime from order or item's alterationDetails
    const dueDate = getOrderDueDate(order);
    const dueTime = getOrderDueTime(order);
    
    // Format the due date display - same logic as modal's order-detail-section
    // This ensures consistency between order-card-header and order-detail-section
    let dueDateDisplay = 'Not Set';
    if (dueDate) {
      const formattedDate = formatDateOnly(dueDate);
      // Only use the formatted date if it's valid (not 'N/A')
      if (formattedDate && formattedDate !== 'N/A') {
        dueDateDisplay = formattedDate;
        // Add time if available
        if (dueTime) {
          const formattedTime = formatTime12Hour(dueTime);
          if (formattedTime && formattedTime !== 'Not Set' && formattedTime !== 'N/A') {
            dueDateDisplay += ` ${formattedTime}`;
          }
        }
      }
      // If formatDateOnly returned 'N/A', keep 'Not Set' as the display value
    }

    const formatAssignedLabel = (label) => {
      if (!label) {
        return 'Unassigned';
      }

      return label
        .split(' ')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const assignedDisplay = formatAssignedLabel(order.assignedUser);
    const statusDisplay = (order.status || 'Order Created').split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    // Get priority from order level or first item's alterationDetails
    const orderPriority = getOrderPriority(order);
    const priorityDisplay = orderPriority !== 'none'
      ? orderPriority.charAt(0).toUpperCase() + orderPriority.slice(1)
      : 'None';

    if (!isCompactView) {
      return (
        <div
          key={order.id}
          className={`order-card full priority-${orderPriority}`}
          onClick={openOnClick ? () => openOrderModal(order) : undefined}
        >
          {orderPriority && orderPriority !== 'none' && (
            <div className={`priority-dot priority-dot-${orderPriority}`}></div>
          )}
          <div className="order-info">
            <div className="order-id">
              <span className="order-label">Order #</span>
              <span className="order-value">{order.id}</span>
            </div>
            <div className="order-receipt">
              <span className="order-label">Receipt #</span>
              <span className="order-value">{order.receiptNumber || 'N/A'}</span>
            </div>
            <div className="order-customer">
              <span className="order-label">Customer</span>
              <span className="order-value customer-name">{order.name}</span>
            </div>
            <div className="order-date">
              <span className="order-label">Date</span>
              <span className="order-value">
                {formatDateOnly(order.timestamp)}
              </span>
            </div>
            <div className="order-alterations">
              <span className="order-label">Items to Alter</span>
              <span className="order-value alteration-count">
                {getAlterationItemCount(order)}
              </span>
            </div>
            <div className="order-due-datetime">
              <span className="order-label">Due Date &amp; Time</span>
              <span className="order-value due-date-time">
                {dueDateDisplay}
              </span>
            </div>
            <div className="order-priority">
              <span className="order-label">Priority</span>
              <span className={`order-value priority-${orderPriority}`}>
                {priorityDisplay}
              </span>
            </div>
            <div className="order-assigned-user">
              <span className="order-label">Assigned To</span>
              <span className={`order-value ${assignedDisplay === 'Unassigned' ? 'unassigned' : ''}`}>
                {assignedDisplay}
              </span>
            </div>
            <div className="order-status">
              <span className="order-label">Status</span>
              <span className={`order-value status-${(order.status || 'order created').replace(/\s+/g, '-')}`}>
                {statusDisplay}
              </span>
            </div>
          </div>
          <div className="order-arrow">→</div>
        </div>
      );
    }

    const isExpanded = !!expandedCards[order.id];

      return (
        <div
          key={order.id}
          className={`order-card compact priority-${orderPriority}`}
          onClick={openOnClick ? () => openOrderModal(order) : undefined}
        >
          {orderPriority && orderPriority !== 'none' && (
            <div className={`priority-dot priority-dot-${orderPriority}`}></div>
          )}
        <div className="order-card-header">
          <div className="order-card-summary">
            <div className="summary-item order-id">
              <span className="order-label">Order #</span>
              <span className="order-value">{order.id}</span>
            </div>
            <div className="order-receipt">
              <span className="order-label">Receipt #</span>
              <span className="order-value">{order.receiptNumber || 'N/A'}</span>
            </div>
            <div className="summary-item order-customer">
              <span className="order-label">Customer</span>
              <span className="order-value">{order.name}</span>
            </div>
            <div className="summary-item order-due-datetime">
              <span className="order-label">Due Date &amp; Time</span>
              <span className="order-value">{dueDateDisplay}</span>
            </div>
            <div className="summary-item order-assigned-user">
              <span className="order-label">Assigned To</span>
              <span className={`order-value ${assignedDisplay === 'Unassigned' ? 'unassigned' : ''}`}>
                {assignedDisplay}
              </span>
            </div>
          </div>

          <div className="order-card-controls">
            <button
              type="button"
              className={`order-card-toggle ${isExpanded ? 'is-expanded' : ''}`}
              onClick={(event) => toggleCardExpansion(order.id, event)}
            >
              <span>{isExpanded ? 'Hide details' : 'View details'}</span>
              <span className="toggle-chevron" aria-hidden="true"></span>
            </button>
            <div className="order-arrow">→</div>
          </div>
        </div>

        <div className={`order-card-details ${isExpanded ? 'expanded' : ''}`}>
          <div className="order-details-grid">
            <div className="summary-item order-id order-id-in-details">
              <span className="order-label">Order #</span>
              <span className="order-value">{order.id}</span>
            </div>
            <div className="order-date">
              <span className="order-label">Date Created</span>
              <span className="order-value">{formatDateOnly(order.timestamp)}</span>
            </div>
            <div className="order-alterations">
              <span className="order-label">Items to Alter</span>
              <span className="order-value alteration-count">
                {getAlterationItemCount(order)}
              </span>
            </div>
            <div className="order-priority">
              <span className="order-label">Priority</span>
              <span className={`order-value priority-${orderPriority}`}>
                {priorityDisplay}
              </span>
            </div>
            <div className="order-status">
              <span className="order-label">Status</span>
              <span className={`order-value status-${(order.status || 'order created').replace(/\s+/g, '-')}`}>
                {statusDisplay}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className="user-dashboard-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-dashboard-container">
        <div className="auth-error">
          <h2>Access Denied</h2>
          <p>You must be logged in to access the User Dashboard.</p>
          <p>Please log in and try again.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-dashboard-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading orders with alterations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-dashboard-container">
        <div className="error-message">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="user-dashboard-container">
      <div className="user-dashboard-header">
        <h1>Orders Requiring Alterations</h1>
        <p>
          {currentView === 'new' && 'New unassigned orders'}
          {currentView === 'assigned' && 'Orders assigned to users'}
          {currentView === 'awaitingPickUp' && 'Orders ready for customer pick up'}
          {currentView === 'completed' && 'Completed alterations (picked up)'}
        </p>
        {!soundUnlockedForSession && !isAdmin && (
          <div className="enable-sound-row">
            <button type="button" className="enable-sound-btn" onClick={unlockSoundForSession}>
              Enable sound for new orders
            </button>
            <span className="enable-sound-hint">Tap once to unlock chime (required on iPad/Safari)</span>
      </div>
        )}

        {/* Navigation Button */}
        <div className="view-navigation">
          <button 
            className={`nav-button ${currentView === 'new' ? 'active' : ''}`}
            onClick={() => {
              if (currentView === 'assigned') {
                setSlideDirection('right');
              } else if (currentView === 'awaitingPickUp') {
                setSlideDirection('right');
              }
              setCurrentView('new');
            }}
          >
            New Orders ({unassignedOrders.length})
          </button>
          <button 
            className={`nav-button ${currentView === 'assigned' ? 'active' : ''}`}
            onClick={() => {
              if (currentView === 'new') {
                setSlideDirection('left');
              } else if (currentView === 'awaitingPickUp') {
                setSlideDirection('right');
              }
              setCurrentView('assigned');
            }}
          >
            Assigned Orders ({assignedOrders.length})
          </button>
          {isAdmin && (
            <>
              <button 
                className={`nav-button ${currentView === 'awaitingPickUp' ? 'active' : ''}`}
                onClick={() => {
                  if (currentView === 'new' || currentView === 'assigned') {
                    setSlideDirection('left');
                  } else if (currentView === 'completed') {
                    setSlideDirection('right');
                  }
                  setCurrentView('awaitingPickUp');
                }}
              >
                Awaiting Pick Up ({awaitingPickUpOrders.length})
              </button>
              <button 
                className={`nav-button ${currentView === 'completed' ? 'active' : ''}`}
                onClick={() => {
                  if (currentView === 'new' || currentView === 'assigned' || currentView === 'awaitingPickUp') {
                    setSlideDirection('left');
                  }
                  setCurrentView('completed');
                  setCompletedOrdersPage(0);
                  setSearchTermCompleted('');
                  setCompletedSelectedIds(new Set());
                  setCompletedSelectionMode(false);
                }}
              >
                Completed Alterations
              </button>
            </>
          )}
        </div>
      </div>

      <div className="slideshow-container">
        <div className={`slide ${currentView === 'new' ? 'active' : ''} ${slideDirection}`}>
          {/* New Orders Section */}
          <div className="orders-section">
            <div className="section-header">
              <h2>New Orders</h2>
              <p>Orders that need to be assigned to a user</p>
              <div className="section-count">
                {searchTermNew ? (
                  <>
                    Showing {filteredUnassignedOrders.length} of {unassignedOrders.length} order{unassignedOrders.length !== 1 ? 's' : ''}
                  </>
                ) : (
                  <>
                    {unassignedOrders.length} order{unassignedOrders.length !== 1 ? 's' : ''}
                  </>
                )}
              </div>
            </div>
            
            {/* Sorting Controls */}
            <div className="sorting-controls">
              <div className="sort-group search-group">
                <label htmlFor="search-new">Search:</label>
                <input
                  id="search-new"
                  type="text"
                  placeholder="Order ID, Barcode ID, Receipt # or Name"
                  value={searchTermNew}
                  onChange={(e) => setSearchTermNew(e.target.value)}
                  className="search-input-control"
                />
                {searchTermNew && (
                  <button
                    onClick={() => setSearchTermNew('')}
                    className="clear-search-btn"
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="sort-group">
                <label htmlFor="sort-new">Sort by:</label>
                <select
                  id="sort-new"
                  value={sortByNew}
                  onChange={(e) => setSortByNew(e.target.value)}
                  className="sort-select"
                >
                  <option value="dateCreated">Date Created</option>
                  <option value="dueDate">Due Date</option>
                  <option value="priority">Priority</option>
                  <option value="customerName">Customer Name</option>
                  <option value="status">Status</option>
                </select>
              </div>
              <div className="sort-group">
                <label htmlFor="order-new">Order:</label>
                <select
                  id="order-new"
                  value={sortOrderNew}
                  onChange={(e) => setSortOrderNew(e.target.value)}
                  className="sort-select"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
            
            {filteredUnassignedOrders.length === 0 ? (
          <div className="no-orders">
                <h3>{searchTermNew ? 'No orders match your search' : 'No new orders'}</h3>
                <p>{searchTermNew ? 'Try a different order ID, barcode ID, receipt number or name' : 'All orders have been assigned to users.'}</p>
          </div>
        ) : (
          <div className="orders-list">
                {sortedUnassignedOrders.map((order) => renderOrderCard(order))}
              </div>
            )}
          </div>
        </div>

        <div className={`slide ${currentView === 'assigned' ? 'active' : ''} ${slideDirection}`}>
          {/* Assigned Orders Section */}
          <div className="orders-section">
            <div className="section-header">
              <h2>Assigned Orders</h2>
              <p>Orders that have been assigned to users</p>
              <div className="section-count">
                {searchTermAssigned ? (
                  <>
                    Showing {filteredAssignedOrders.length} of {assignedOrders.length} order{assignedOrders.length !== 1 ? 's' : ''}
                  </>
                ) : (
                  <>
                    {assignedOrders.length} order{assignedOrders.length !== 1 ? 's' : ''}
                  </>
                )}
              </div>
            </div>
            
            {/* Sorting Controls */}
            <div className="sorting-controls">
              <div className="sort-group search-group">
                <label htmlFor="search-assigned">Search:</label>
                <input
                  id="search-assigned"
                  type="text"
                  placeholder="Order ID, Barcode ID, Receipt # or Name"
                  value={searchTermAssigned}
                  onChange={(e) => setSearchTermAssigned(e.target.value)}
                  className="search-input-control"
                />
                {searchTermAssigned && (
                  <button
                    onClick={() => setSearchTermAssigned('')}
                    className="clear-search-btn"
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
                  </div>
              <div className="sort-group">
                <label htmlFor="sort-assigned">Sort by:</label>
                <select
                  id="sort-assigned"
                  value={sortByAssigned}
                  onChange={(e) => setSortByAssigned(e.target.value)}
                  className="sort-select"
                >
                  <option value="dateCreated">Date Created</option>
                  <option value="dueDate">Due Date</option>
                  <option value="priority">Priority</option>
                  <option value="customerName">Customer Name</option>
                  <option value="assignedUser">Assigned User</option>
                  <option value="status">Status</option>
                </select>
                  </div>
              <div className="sort-group">
                <label htmlFor="order-assigned">Order:</label>
                <select
                  id="order-assigned"
                  value={sortOrderAssigned}
                  onChange={(e) => setSortOrderAssigned(e.target.value)}
                  className="sort-select"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
                  </div>
                </div>
            
            {filteredAssignedOrders.length === 0 ? (
              <div className="no-orders">
                <h3>{searchTermAssigned ? 'No orders match your search' : 'No assigned orders'}</h3>
                <p>{searchTermAssigned ? 'Try a different order ID, barcode ID, receipt number or name' : 'No orders have been assigned to users yet.'}</p>
              </div>
            ) : (
              <div className="orders-list">
                {sortedAssignedOrders.map((order) => renderOrderCard(order))}
              </div>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className={`slide ${currentView === 'awaitingPickUp' ? 'active' : ''} ${slideDirection}`}>
            {/* Awaiting Pick Up Orders Section */}
            <div className="orders-section">
              <div className="section-header">
                <h2>Awaiting Pick Up</h2>
                <p>Orders that are ready for customer pick up</p>
                <div className="section-header-actions">
                  <div className="section-count">
                    {searchTermAwaitingPickUp ? (
                      <>
                        Showing {filteredAwaitingPickUpOrders.length} of {awaitingPickUpOrders.length} order{awaitingPickUpOrders.length !== 1 ? 's' : ''}
                      </>
                    ) : (
                      <>
                        {awaitingPickUpOrders.length} order{awaitingPickUpOrders.length !== 1 ? 's' : ''}
                      </>
                    )}
                  </div>
                  {sortedAwaitingPickUpOrders.length > 0 && (
                    <button
                      type="button"
                      className="push-notification-btn"
                      onClick={() => {
                        setPushNotificationSelectedIds(new Set());
                        setPushNotificationResult(null);
                        setPushNotificationSearchTerm('');
                        setShowPushNotificationModal(true);
                      }}
                    >
                      Push Notification
                    </button>
                  )}
                </div>
              </div>
              
              {/* Sorting Controls */}
              <div className="sorting-controls">
                <div className="sort-group search-group">
                  <label htmlFor="search-awaiting">Search:</label>
                  <input
                    id="search-awaiting"
                    type="text"
                    placeholder="Order ID, Barcode ID, Receipt # or Name"
                    value={searchTermAwaitingPickUp}
                    onChange={(e) => setSearchTermAwaitingPickUp(e.target.value)}
                    className="search-input-control"
                  />
                  {searchTermAwaitingPickUp && (
                    <button
                      onClick={() => setSearchTermAwaitingPickUp('')}
                      className="clear-search-btn"
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="sort-group">
                  <label htmlFor="sort-awaiting">Sort by:</label>
                  <select
                    id="sort-awaiting"
                    value={sortByAwaitingPickUp}
                    onChange={(e) => setSortByAwaitingPickUp(e.target.value)}
                    className="sort-select"
                  >
                    <option value="dateCreated">Date Created</option>
                    <option value="dueDate">Due Date</option>
                    <option value="priority">Priority</option>
                    <option value="customerName">Customer Name</option>
                    <option value="assignedUser">Assigned User</option>
                    <option value="status">Status</option>
                  </select>
                </div>
                <div className="sort-group">
                  <label htmlFor="order-awaiting">Order:</label>
                  <select
                    id="order-awaiting"
                    value={sortOrderAwaitingPickUp}
                    onChange={(e) => setSortOrderAwaitingPickUp(e.target.value)}
                    className="sort-select"
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>
              </div>
              
              {filteredAwaitingPickUpOrders.length === 0 ? (
                <div className="no-orders">
                  <h3>{searchTermAwaitingPickUp ? 'No orders match your search' : 'No orders awaiting pick up'}</h3>
                  <p>{searchTermAwaitingPickUp ? 'Try a different order ID, barcode ID, receipt number or name' : 'No orders are currently ready for pick up.'}</p>
                </div>
              ) : (
                <div className="orders-list">
                  {sortedAwaitingPickUpOrders.map((order) => renderOrderCard(order))}
                </div>
              )}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className={`slide ${currentView === 'completed' ? 'active' : ''} ${slideDirection}`}>
            {/* Completed Alterations Section */}
            <div className="orders-section">
              <div className="section-header">
                <h2>Completed Alterations</h2>
                <p>Orders that have been picked up by customers</p>
                <div className="section-count">
                  {searchTermCompleted ? (
                    <>Showing {displayedCompletedOrders.length} of {filteredCompletedOrders.length} (filtered from {completedOrders.length})</>
                  ) : (
                    <>Showing {displayedCompletedOrders.length} of {completedOrders.length} completed order{completedOrders.length !== 1 ? 's' : ''}</>
                  )}
                </div>
              </div>

              <div className="sorting-controls">
                <div className="sort-group search-group">
                  <label htmlFor="search-completed">Search:</label>
                  <input
                    id="search-completed"
                    type="text"
                    placeholder="Order ID, Barcode ID, Receipt # or Name"
                    value={searchTermCompleted}
                    onChange={(e) => {
                      setSearchTermCompleted(e.target.value);
                      setCompletedOrdersPage(0);
                    }}
                    className="search-input-control"
                  />
                  {searchTermCompleted && (
                    <button
                      onClick={() => {
                        setSearchTermCompleted('');
                        setCompletedOrdersPage(0);
                      }}
                      className="clear-search-btn"
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {displayedCompletedOrders.length > 0 && (
                <div className="completed-bulk-actions">
                  <button
                    type="button"
                    className={`nav-btn completed-select-mode-btn ${completedSelectionMode ? 'active' : ''}`}
                    onClick={() => setCompletedSelectionMode(prev => !prev)}
                    disabled={bulkStatusSending}
                  >
                    {completedSelectionMode ? 'Done' : 'Select'}
                  </button>
                  {completedSelectionMode && (
                    <span className="completed-bulk-hint">Click an order card to select or deselect it.</span>
                  )}
                  <span className="completed-bulk-label">Change status for selected:</span>
                  <select
                    value={bulkStatusCompleted}
                    onChange={(e) => setBulkStatusCompleted(e.target.value)}
                    className="completed-bulk-status-select"
                    disabled={bulkStatusSending}
                  >
                    <option value="order created">Order Created</option>
                    <option value="starting alterations">Starting Alterations</option>
                    <option value="ready for pick up">Ready for Pick Up</option>
                    <option value="picked up">Picked Up</option>
                  </select>
                  <button
                    type="button"
                    className="nav-btn completed-bulk-apply-btn"
                    onClick={applyBulkStatusCompleted}
                    disabled={completedSelectedIds.size === 0 || bulkStatusSending}
                  >
                    {bulkStatusSending ? 'Updating...' : `Apply to ${completedSelectedIds.size} order${completedSelectedIds.size !== 1 ? 's' : ''}`}
                  </button>
                  <button type="button" className="completed-bulk-select-all" onClick={selectAllCompletedOnPage} disabled={bulkStatusSending}>
                    Select all on page
                  </button>
                  {completedSelectedIds.size > 0 && (
                    <button type="button" className="completed-bulk-clear" onClick={clearCompletedSelection} disabled={bulkStatusSending}>
                      Clear selection
                    </button>
                  )}
                </div>
              )}
              
              {displayedCompletedOrders.length === 0 ? (
                <div className="no-orders">
                  <h3>{searchTermCompleted ? 'No orders match your search' : 'No completed orders'}</h3>
                  <p>{searchTermCompleted ? 'Try a different order ID, barcode ID, receipt number or name' : 'No orders have been marked as picked up yet.'}</p>
                </div>
              ) : (
                <>
                  <div className="orders-list completed-orders-list">
                    {displayedCompletedOrders.map((order) => (
              <div
                key={order.id}
                        className={`completed-order-card-wrap ${completedSelectedIds.has(order.id) ? 'selected' : ''}`}
                        onClick={completedSelectionMode ? () => toggleCompletedSelection(order.id) : undefined}
              >
                        {renderOrderCard(order, { openOnClick: !completedSelectionMode })}
                  </div>
                    ))}
                  </div>
                  
                  {hasMoreCompletedOrders && (
                    <div className="load-more-container" style={{ marginTop: '20px', textAlign: 'center' }}>
                      <button
                        className="nav-btn"
                        onClick={() => setCompletedOrdersPage(prev => prev + 1)}
                      >
                        Load Next 10 Orders
                      </button>
                  </div>
                  )}
                  
                  {completedOrdersPage > 0 && (
                    <div className="load-more-container" style={{ marginTop: '10px', textAlign: 'center' }}>
                      <button
                        className="nav-btn"
                        onClick={() => setCompletedOrdersPage(prev => Math.max(0, prev - 1))}
                      >
                        ← Previous 10 Orders
                      </button>
                </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Receipt Lookup Modal (same structure/size/functions as admin order modal) */}
      {showReceiptLookupModal && (
        <div className="modal-overlay receipt-lookup-modal" onClick={closeReceiptLookupModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{receiptLookupOrder ? 'Select items needing alteration' : 'Look up by receipt number'}</h2>
              <button className="modal-close" onClick={closeReceiptLookupModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              {receiptLookupOrder ? (
                <>
                  <div className="order-detail-section">
                    <h3>Order</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Receipt #</span>
                        <span className="detail-value">{receiptLookupOrder.receiptNumber || receiptLookupOrder.id}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Customer</span>
                        <span className="detail-value">{receiptLookupOrder.name || '—'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="order-detail-section">
                    <h3>Line Items</h3>
                    {receiptLookupLineItems.length === 0 ? (
                      <p className="no-items">No line items on this order.</p>
                    ) : (
                      <div className="line-items-list">
                        {receiptLookupLineItems.map((item, index) => (
                          <div key={index} className="line-item">
                            <div className="item-main-row">
                              <div className="item-details">
                                <span className="item-name">{item.name || 'Unnamed Item'}</span>
                                <span className="item-quantity">Qty: {item.quantity || 1}</span>
                              </div>
                              <div className="item-actions">
                                <button
                                  type="button"
                                  className={`alteration-checkbox ${item.needsAlteration ? 'checked' : ''}`}
                                  onClick={() => toggleReceiptLookupItemNeedsAlteration(index)}
                                  title={item.needsAlteration ? 'Mark as no alteration needed' : 'Mark for alteration'}
                                >
                                  {item.needsAlteration ? '✓' : ''}
                                </button>
                                <span className="alteration-label">Alteration</span>
                              </div>
                            </div>
                            {item.needsAlteration && (
                              <div className="alteration-details-dropdown">
                                <div className="alteration-header">
                                  <button
                                    type="button"
                                    className="toggle-details-btn"
                                    onClick={() => setExpandedReceiptLookupItem(expandedReceiptLookupItem === index ? null : index)}
                                  >
                                    {expandedReceiptLookupItem === index ? '▼' : '▶'} Alteration Details
                                  </button>
                                </div>
                                <div className="alteration-notes-row">
                                  <label htmlFor={`receipt-lookup-notes-${index}`} className="alteration-notes-label">Notes</label>
                                  <textarea
                                    id={`receipt-lookup-notes-${index}`}
                                    className="alteration-notes-input"
                                    value={item.alterationDetails?.notes ?? ''}
                                    onChange={(e) => updateReceiptLookupAlteration(index, 'notes', e.target.value)}
                                    placeholder="Add notes for this alteration item..."
                                    rows={2}
                                  />
                                </div>
                                {expandedReceiptLookupItem === index && (
                                  <div className="alteration-details-content">
                                    <div className="alteration-sections">
                                      {Object.entries(RECEIPT_LOOKUP_ALTERATIONS).map(([sectionName, options]) => (
                                        <div key={sectionName} className="alteration-section">
                                          <h4 className="section-title">{sectionName}</h4>
                                          <div className="section-buttons">
                                            {options.map((opt) => (
                                              <div key={opt.key} className="alteration-button-group">
                                                <button
                                                  type="button"
                                                  className={`alteration-btn ${item.alterationDetails?.[opt.key] ? 'active' : ''}`}
                                                  onClick={() => updateReceiptLookupAlteration(index, opt.key, !item.alterationDetails?.[opt.key])}
                                                >
                                                  {opt.label}
                                                </button>
                                                {opt.valueKey && item.alterationDetails?.[opt.key] && (
                                                  <input
                                                    type="number"
                                                    className="alteration-input"
                                                    placeholder="Enter number"
                                                    min="0"
                                                    step="1"
                                                    value={item.alterationDetails?.[opt.valueKey] ?? ''}
                                                    onChange={(e) => updateReceiptLookupAlteration(index, opt.valueKey, e.target.value)}
                                                  />
                                                )}
                                                {opt.valueKeys && item.alterationDetails?.[opt.key] && (
                                                  <div className="length-inputs">
                                                    <input
                                                      type="number"
                                                      className="alteration-input"
                                                      placeholder="Shorter"
                                                      min="0"
                                                      step="1"
                                                      value={item.alterationDetails?.[opt.valueKeys[0]] ?? ''}
                                                      onChange={(e) => updateReceiptLookupAlteration(index, opt.valueKeys[0], e.target.value)}
                                                    />
                                                    <input
                                                      type="number"
                                                      className="alteration-input"
                                                      placeholder="Longer"
                                                      min="0"
                                                      step="1"
                                                      value={item.alterationDetails?.[opt.valueKeys[1]] ?? ''}
                                                      onChange={(e) => updateReceiptLookupAlteration(index, opt.valueKeys[1], e.target.value)}
                                                    />
                                                  </div>
                                                )}
              </div>
            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
          </div>
        )}
      </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {receiptLookupError && <p className="receipt-lookup-error">{receiptLookupError}</p>}
                </>
              ) : (
                <div className="order-detail-section">
                  <h3>Look up order</h3>
                  <div className="order-controls">
                    <div className="order-control-field">
                      <label htmlFor="receipt-lookup-input">Receipt number</label>
                      <input
                        id="receipt-lookup-input"
                        type="text"
                        placeholder="Enter receipt number"
                        value={receiptLookup}
                        onChange={(e) => {
                          setReceiptLookup(e.target.value);
                          setReceiptLookupError('');
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleReceiptLookup()}
                        disabled={receiptLookupLoading}
                      />
                    </div>
                    <button
                      type="button"
                      className="modal-btn modal-btn-save"
                      onClick={handleReceiptLookup}
                      disabled={receiptLookupLoading}
                    >
                      {receiptLookupLoading ? 'Finding…' : 'Find order'}
                    </button>
                  </div>
                  {receiptLookupError && <p className="receipt-lookup-error">{receiptLookupError}</p>}
                </div>
              )}
            </div>
            {receiptLookupOrder && (
              <div className="modal-footer">
                <div className="modal-actions">
                  <button
                    type="button"
                    className="modal-btn"
                    onClick={() => {
                      setReceiptLookupOrder(null);
                      setReceiptLookupLineItems([]);
                      setExpandedReceiptLookupItem(null);
                      setReceiptLookupError('');
                    }}
                    disabled={receiptLookupLoading}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="modal-btn modal-btn-save"
                    onClick={handleReceiptLookupContinue}
                    disabled={receiptLookupLoading}
                  >
                    {receiptLookupLoading ? 'Saving…' : 'Continue to order'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeOrderModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details - Alterations Only</h2>
              <div className="modal-header-actions">
                {isAdmin && (
                  <button
                    type="button"
                    className="cross-dashboard-order-btn"
                    onClick={goToAdminOrderView}
                    title="Open this order in the admin dashboard"
                  >
                    Admin view
                  </button>
                )}
                <button 
                  className="measurements-toggle-btn"
                  onClick={toggleMeasurements}
                  title={showMeasurements ? "Hide Measurements" : "View/Edit Measurements"}
                >
                  {showMeasurements ? '📏 Hide Measurements' : '📏 Measurements'}
                </button>
              <button className="modal-close" onClick={closeOrderModal}>
                ×
              </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="order-detail-section">
                <h3>Order Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Order ID:</span>
                    <span className="detail-value">{selectedOrder.id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Receipt Number:</span>
                    <span className="detail-value">{selectedOrder.receiptNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Customer Name:</span>
                    <span className="detail-value">{selectedOrder.name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{formatPhoneNumber(selectedOrder.phone)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Date Placed:</span>
                    <span className="detail-value">
                      {formatTimestamp(selectedOrder.timestamp)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="order-detail-section">
                <h3>Order Details</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Due Date:</span>
                    <span className="detail-value">
                      {(() => {
                        const dueDate = getOrderDueDate(selectedOrder);
                        if (!dueDate) return 'Not Set';
                        const formatted = formatDateOnly(dueDate);
                        return (formatted && formatted !== 'N/A') ? formatted : 'Not Set';
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Due Time:</span>
                    <span className="detail-value">
                      {formatTime12Hour(getOrderDueTime(selectedOrder))}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Priority:</span>
                    {isAdmin ? (
                      <div className="priority-field">
                        <select
                          value={getOrderPriority(selectedOrder)}
                          onChange={(e) => updateAlterationDetail('priority', e.target.value)}
                          className={`priority-select priority-${getOrderPriority(selectedOrder)}`}
                        >
                          <option value="none">None</option>
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    ) : (
                      <span className={`detail-value priority-${getOrderPriority(selectedOrder)}`}>
                        {(() => {
                          const priority = getOrderPriority(selectedOrder);
                          return priority !== 'none' ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'None';
                        })()}
                      </span>
                    )}
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Assigned To:</span>
                    <div className="assignment-field">
                      <select
                        value={selectedOrder.assignedUser || ''}
                        onChange={(e) => updateAlterationDetail('assignedUser', e.target.value)}
                        className="assignment-select"
                      >
                        <option value="">Unassigned</option>
                        {users.filter(user => user.role !== 'Admin').map((user) => (
                          <option key={user.id} value={user.name}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <div className="status-field">
                      <select
                        value={selectedOrder.status || 'order created'}
                        onChange={(e) => updateAlterationDetail('status', e.target.value)}
                        className={`status-select status-${(selectedOrder.status || 'order created').replace(/\s+/g, '-')}`}
                      >
                        <option value="order created">Order Created</option>
                        <option value="starting alterations">Starting Alterations</option>
                        <option value="ready for pick up">Ready for Pick Up</option>
                        {isAdmin && (
                          <option value="picked up">Picked Up</option>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="order-detail-section">
                <h3>Items Requiring Alterations</h3>
                {selectedOrder.line_items && Array.isArray(selectedOrder.line_items) ? (
                  <div className="line-items-list">
                    {selectedOrder.line_items
                      .map((item, realIndex) => ({ item, realIndex }))
                      .filter(({ item }) => item.needsAlteration === true)
                      .map(({ item, realIndex }) => {
                        const inSplitMode = isSplitModeFor(selectedOrder.id, realIndex);
                        const tailors = users.filter(u => u.role !== 'Admin');
                        return (
                        <div key={realIndex} className="line-item alteration-item">
                          <div className="alteration-item-header">
                          <div className="item-details">
                            <span className="item-name">{item.name || 'Unnamed Item'}</span>
                            <span className="item-quantity">Qty: {item.quantity || 1}</span>
                          </div>
                            <div className="alteration-item-actions">
                              <button
                                type="button"
                                className={`split-select-btn ${inSplitMode ? 'active' : ''}`}
                                onClick={() => openSplitMode(selectedOrder.id, realIndex)}
                              >
                                {inSplitMode ? 'Done' : 'Assign'}
                              </button>
                              {inSplitMode && (
                                <>
                                  <select
                                    className="split-tailor-select"
                                    value={splitAssignTailorId}
                                    onChange={(e) => setSplitAssignTailorId(e.target.value)}
                                  >
                                    <option value="">Assign to tailor…</option>
                                    {tailors.map((u) => (
                                      <option key={u.id} value={u.id}>{u.name}</option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="split-assign-btn"
                                    disabled={selectedAlterationKeysForSplit.size === 0 || !splitAssignTailorId || splitAssignSaving}
                                    onClick={assignSelectedToTailor}
                                  >
                                    {splitAssignSaving ? 'Saving…' : 'Assign'}
                                  </button>
                                </>
                              )}
                                  </div>
                          </div>
                          {inSplitMode && getAlterationKeysWithLabels(item).length > 0 && (
                            <div className="alteration-split-chips">
                              {getAlterationKeysWithLabels(item).map(({ key, label }) => {
                                const selected = selectedAlterationKeysForSplit.has(key);
                                const assignedUserId = item.alterationAssignments?.[key];
                                const assignedName = assignedUserId ? (tailors.find(t => t.id === assignedUserId)?.name || assignedUserId) : null;
                                return (
                                  <button
                                    key={key}
                                    type="button"
                                    className={`alteration-chip ${selected ? 'selected' : ''}`}
                                    onClick={() => toggleSplitAlterationKey(key)}
                                  >
                                    {label}
                                    {assignedName && <span className="chip-assigned"> → {assignedName}</span>}
                                  </button>
                                );
                              })}
                                  </div>
                                )}
                          {item.alterationDetails && (
                              <div className="alteration-types">
                                {item.alterationDetails.useCustomerMeasurements && (
                                  <div className="use-measurements-notice">
                                    <strong>⚠️ Use Customer's Saved Measurements</strong>
                                  </div>
                                )}
                                {(() => {
                                  const notesParts = [];
                                  const pushIf = (label, value) => {
                                    if (value && typeof value === 'string' && value.trim()) {
                                      notesParts.push(label ? `${label}: ${value.trim()}` : value.trim());
                                    }
                                  };
                                  // New per-section notes
                                  pushIf('Jacket', item.alterationDetails.jacketNotes);
                                  pushIf('Pants', item.alterationDetails.pantsNotes);
                                  pushIf('Shirt', item.alterationDetails.shirtNotes);
                                  pushIf('Vest', item.alterationDetails.vestNotes);
                                  // Backward-compatibility for older global notes
                                  pushIf('', item.alterationDetails.notes);
                                  if (!notesParts.length) return null;
                                  return (
                                    <div className="alteration-notes-display">
                                      <strong>Notes:</strong> {notesParts.join(' | ')}
                              </div>
                                  );
                                })()}
                                {/* Jacket Section */}
                                {(() => {
                                  const jacketItems = [];
                                  if (item.alterationDetails.sleeveLonger) {
                                    const value = item.alterationDetails.sleeveLongerValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Sleeve Longer: ${value}` : 'Sleeve Longer');
                                  }
                                  if (item.alterationDetails.sleeveShorter) {
                                    const value = item.alterationDetails.sleeveShorterValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Sleeve Shorter: ${value}` : 'Sleeve Shorter');
                                  }
                                  if (item.alterationDetails.takeIn) {
                                    const value = item.alterationDetails.takeInValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Take In: ${value}` : 'Take In');
                                  }
                                  if (item.alterationDetails.takeOut) {
                                    const value = item.alterationDetails.takeOutValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Take Out: ${value}` : 'Take Out');
                                  }
                                  if (item.alterationDetails.shorterJacket) {
                                    const value = item.alterationDetails.shorterJacketValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Shorter Jacket: ${value}` : 'Shorter Jacket');
                                  }
                                  if (item.alterationDetails.shoulderPads) {
                                    jacketItems.push('Shoulder Pads');
                                  }
                                  if (item.alterationDetails.functionButtonHole) {
                                    jacketItems.push('Functional Button Hole');
                                  }
                                  if (item.alterationDetails.changeButtonsJacket) {
                                    jacketItems.push('Change Buttons');
                                  }
                                  if (item.alterationDetails.taperSleeve) {
                                    const value = item.alterationDetails.taperSleeveValue;
                                    jacketItems.push(value && parseInt(value) !== 0 ? `Taper Sleeve: ${value}` : 'Taper Sleeve');
                                  }
                                  if (item.alterationDetails.shattnezTest) {
                                    jacketItems.push('Shattnez Test');
                                  }
                                  if (item.alterationDetails.removeShattnez) {
                                    jacketItems.push('Remove Shattnez');
                                  }
                                  return jacketItems.length > 0 ? (
                                    <div className="alteration-section-display">
                                      <strong>Jacket:</strong> {jacketItems.join(', ')}
                                  </div>
                                  ) : null;
                                })()}

                                {/* Pants Section */}
                                {(() => {
                                  const pantsItems = [];
                                  if (item.alterationDetails.hemming) {
                                    const value = item.alterationDetails.hemmingValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Hemming: ${value}` : 'Hemming');
                                  }
                                  if (item.alterationDetails.taperPants) {
                                    const value = item.alterationDetails.taperPantsValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Taper Pants: ${value}` : 'Taper Pants');
                                  }
                                  if (item.alterationDetails.waistIn) {
                                    const value = item.alterationDetails.waistInValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Waist In: ${value}` : 'Waist In');
                                  }
                                  if (item.alterationDetails.waistOut) {
                                    const value = item.alterationDetails.waistOutValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Waist Out: ${value}` : 'Waist Out');
                                  }
                                  if (item.alterationDetails.seatIn) {
                                    const value = item.alterationDetails.seatInValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Seat In: ${value}` : 'Seat In');
                                  }
                                  if (item.alterationDetails.seatOut) {
                                    const value = item.alterationDetails.seatOutValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Seat Out: ${value}` : 'Seat Out');
                                  }
                                  if (item.alterationDetails.crotchIn) {
                                    const value = item.alterationDetails.crotchInValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Crotch In: ${value}` : 'Crotch In');
                                  }
                                  if (item.alterationDetails.crotchOut) {
                                    const value = item.alterationDetails.crotchOutValue;
                                    pantsItems.push(value && parseInt(value) !== 0 ? `Crotch Out: ${value}` : 'Crotch Out');
                                  }
                                  if (item.alterationDetails.crotchLining) {
                                    pantsItems.push('Crotch Lining');
                                  }
                                  if (item.alterationDetails.changeZipper) {
                                    pantsItems.push('Change Zipper');
                                  }
                                  if (item.alterationDetails.length) {
                                    const lengthParts = [];
                                    if (item.alterationDetails.lengthShorter && parseInt(item.alterationDetails.lengthShorter) !== 0) {
                                      lengthParts.push(`Shorter: ${item.alterationDetails.lengthShorter}`);
                                    }
                                    if (item.alterationDetails.lengthLonger && parseInt(item.alterationDetails.lengthLonger) !== 0) {
                                      lengthParts.push(`Longer: ${item.alterationDetails.lengthLonger}`);
                                    }
                                    pantsItems.push(lengthParts.length > 0 ? `Length (${lengthParts.join(', ')})` : 'Length');
                                  }
                                  if (item.alterationDetails.changeButtons) {
                                    pantsItems.push('Change Buttons');
                                  }
                                  if (item.alterationDetails.suspenderButtons) {
                                    pantsItems.push('Suspender Buttons');
                                  }
                                  return pantsItems.length > 0 ? (
                                    <div className="alteration-section-display">
                                      <strong>Pants:</strong> {pantsItems.join(', ')}
                                </div>
                                  ) : null;
                                })()}

                                {/* Shirt Section */}
                                {(() => {
                                  const shirtItems = [];
                                  if (item.alterationDetails.shorterSleeve) {
                                    const value = item.alterationDetails.shorterSleeveValue;
                                    shirtItems.push(value && parseInt(value) !== 0 ? `Shorter Sleeve: ${value}` : 'Shorter Sleeve');
                                  }
                                  if (item.alterationDetails.takeInShirt) {
                                    const value = item.alterationDetails.takeInShirtValue;
                                    shirtItems.push(value && parseInt(value) !== 0 ? `Take In: ${value}` : 'Take In');
                                  }
                                  if (item.alterationDetails.takeOutShirt) {
                                    const value = item.alterationDetails.takeOutShirtValue;
                                    shirtItems.push(value && parseInt(value) !== 0 ? `Take Out: ${value}` : 'Take Out');
                                  }
                                  if (item.alterationDetails.iron) {
                                    shirtItems.push('Iron');
                                  }
                                  return shirtItems.length > 0 ? (
                                    <div className="alteration-section-display">
                                      <strong>Shirt:</strong> {shirtItems.join(', ')}
                              </div>
                                  ) : null;
                                })()}

                                {/* Vest Section */}
                                {(() => {
                                  const vestItems = [];
                                  if (item.alterationDetails.changeButtonsVest) {
                                    vestItems.push('Change Buttons');
                                  }
                                  if (item.alterationDetails.takeInVest) {
                                    const value = item.alterationDetails.takeInVestValue;
                                    vestItems.push(value && parseInt(value) !== 0 ? `Take In: ${value}` : 'Take In');
                                  }
                                  if (item.alterationDetails.takeOutVest) {
                                    const value = item.alterationDetails.takeOutVestValue;
                                    vestItems.push(value && parseInt(value) !== 0 ? `Take Out: ${value}` : 'Take Out');
                                  }
                                  return vestItems.length > 0 ? (
                                    <div className="alteration-section-display">
                                      <strong>Vest:</strong> {vestItems.join(', ')}
                            </div>
                                  ) : null;
                                })()}
                                {item.alterationAssignments && Object.keys(item.alterationAssignments).length > 0 && (
                                  <div className="alteration-split-display">
                                    <strong>Assigned:</strong>
                                    <ul className="alteration-split-list">
                                      {Object.entries(item.alterationAssignments).map(([key, assignedUserId]) => {
                                        const label = ALTERATION_KEY_LABELS[key] || key;
                                        const tailor = users.find((u) => u.id === assignedUserId);
                                        const name = tailor?.name || assignedUserId;
                                        const isFinished = item.alterationFinished?.[key];
                                        return (
                                          <li key={key} className="alteration-split-row">
                                            <span className="alteration-split-label">{label} → {name}{isFinished ? ' ✓' : ''}</span>
                                            <button
                                              type="button"
                                              className="alteration-unassign-btn"
                                              onClick={() => unassignAlteration(selectedOrder.id, realIndex, key)}
                                              title="Remove assignment"
                                            >
                                              Remove
                                            </button>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                    {Object.keys(item.alterationAssignments).some((key) => !item.alterationFinished?.[key]) ? (
                                      <button
                                        type="button"
                                        className="alteration-mark-finished-btn"
                                        onClick={() => markAllAlterationsFinishedForItem(selectedOrder.id, realIndex)}
                                      >
                                        Mark all finished
                                      </button>
                                    ) : (
                                      <span className="alteration-finished-badge">All finished</span>
                          )}
                        </div>
                                )}
                        </div>
                          )}
                        </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className="no-items">No line items found</p>
                )}
              </div>

              {/* Customer Measurements Section */}
              {showMeasurements && (
                <div className="alterations-section" ref={measurementsRef}>
                  <h3>Customer Alteration Measurements</h3>
                  
                  {/* Jacket Measurements Section */}
                  <div className="measurement-group">
                    <h4 className="measurement-group-title">Jacket Measurements</h4>
                    <div className="measurement-grid">
                      <div className="measurement-field">
                        <label className="measurement-label">Back Length</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.backLength || ''}
                          onChange={(e) => updateCustomerMeasurement('backLength', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Shoulder Width</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shoulderWidth || ''}
                          onChange={(e) => updateCustomerMeasurement('shoulderWidth', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Outsleeve L</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.outsleeveL || ''}
                          onChange={(e) => updateCustomerMeasurement('outsleeveL', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Outsleeve R</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.outsleeveR || ''}
                          onChange={(e) => updateCustomerMeasurement('outsleeveR', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Chest</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.chest || ''}
                          onChange={(e) => updateCustomerMeasurement('chest', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Stomach</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.stomach || ''}
                          onChange={(e) => updateCustomerMeasurement('stomach', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Seat</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.seat || ''}
                          onChange={(e) => updateCustomerMeasurement('seat', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Upperarm</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.upperarm || ''}
                          onChange={(e) => updateCustomerMeasurement('upperarm', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Trouser Measurements Section */}
                  <div className="measurement-group">
                    <h4 className="measurement-group-title">Trouser Measurements</h4>
                    <div className="measurement-grid">
                      <div className="measurement-field">
                        <label className="measurement-label">Waist</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.waist || ''}
                          onChange={(e) => updateCustomerMeasurement('waist', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Seat</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.trouserSeat || ''}
                          onChange={(e) => updateCustomerMeasurement('trouserSeat', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Outseam L</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.outseamL || ''}
                          onChange={(e) => updateCustomerMeasurement('outseamL', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Outseam R</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.outseamR || ''}
                          onChange={(e) => updateCustomerMeasurement('outseamR', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Crotch</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.crotch || ''}
                          onChange={(e) => updateCustomerMeasurement('crotch', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Knee</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.knee || ''}
                          onChange={(e) => updateCustomerMeasurement('knee', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Foot</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.foot || ''}
                          onChange={(e) => updateCustomerMeasurement('foot', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Rise</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.rise || ''}
                          onChange={(e) => updateCustomerMeasurement('rise', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Shirt Measurements Section */}
                  <div className="measurement-group">
                    <h4 className="measurement-group-title">Shirt Measurements</h4>
                    <div className="measurement-grid">
                      <div className="measurement-field">
                        <label className="measurement-label">Neck</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtNeck || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtNeck', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Chest</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtChest || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtChest', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Waist</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtWaist || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtWaist', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Seat/Hips</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtSeatHips || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtSeatHips', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Shoulders</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtShoulders || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtShoulders', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Sleeve Length</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtSleeveLength || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtSleeveLength', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Biceps/Upper Arm</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtBicepsUpperArm || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtBicepsUpperArm', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Cuff</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtCuff || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtCuff', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Shirt Length (Back)</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtLengthBack || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtLengthBack', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Yoke Width</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtYokeWidth || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtYokeWidth', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Front Length</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtFrontLength || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtFrontLength', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Collar Spread</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerMeasurements.shirtCollarSpread || ''}
                          onChange={(e) => updateCustomerMeasurement('shirtCollarSpread', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="alteration-actions">
                    <button 
                      className="save-alterations-btn"
                      onClick={saveCustomerMeasurements}
                      disabled={savingMeasurements}
                    >
                      {savingMeasurements ? 'Saving...' : 'Save Measurements'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {Object.keys(pendingChanges).length > 0 && (
                <div className="pending-changes-indicator">
                  <span className="pending-changes-text">
                    {Object.keys(pendingChanges).length} pending change(s)
                  </span>
                </div>
              )}
              <div className="modal-buttons">
                {Object.keys(pendingChanges).length > 0 && (
                  <button 
                    className="modal-btn save-btn" 
                    onClick={saveAlterationChanges}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              <button className="modal-btn" onClick={closeOrderModal}>
                Close
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Push Notification Modal (Awaiting Pick Up - resend ready for pick up SMS) */}
      {showPushNotificationModal && (
        <div className="modal-overlay" onClick={() => {
          if (pushModalBusy || showPushNotificationCombinedConfirm) return;
          setShowPushNotificationModal(false);
        }}>
          <div className="modal-content push-notification-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Push Notification</h2>
              <button type="button" className="modal-close-btn" onClick={() => {
                if (pushModalBusy || showPushNotificationCombinedConfirm) return;
                setShowPushNotificationModal(false);
              }} aria-label="Close">×</button>
            </div>
            <div className="push-notification-body">
            <p className="push-notification-description">Select orders to resend the &quot;ready for pick up&quot; SMS notification to the customer. Use Notify &amp; complete the rest to notify selected orders and mark the rest as picked up.</p>
            <div className="push-notification-search-actions-row">
              <div className="push-notification-search-row">
                <label htmlFor="push-notification-search" className="push-notification-search-label">Search:</label>
                <input
                  id="push-notification-search"
                  type="text"
                  className="push-notification-search-input"
                  placeholder="Search by order ID, barcode ID, receipt # or name..."
                  value={pushNotificationSearchTerm}
                  onChange={(e) => setPushNotificationSearchTerm(e.target.value)}
                />
                {pushNotificationSearchTerm && (
                  <button type="button" className="push-notification-search-clear" onClick={() => setPushNotificationSearchTerm('')} title="Clear search">×</button>
                )}
              </div>
              <div className="push-notification-actions">
                <button type="button" className="push-notification-select-btn" onClick={() => setPushNotificationSelectedIds(new Set(visiblePushNotificationOrders.map(o => o.id)))}>Select all</button>
                <button type="button" className="push-notification-select-btn" onClick={() => setPushNotificationSelectedIds(new Set())}>Clear</button>
              </div>
            </div>
            {pushNotificationSearchTerm.trim() && (
              <p className="push-notification-search-count">
                Showing {visiblePushNotificationOrders.length} of {sortedAwaitingPickUpOrders.length} orders
              </p>
            )}
            <div className="push-notification-list">
              {visiblePushNotificationOrders
                .map((order) => {
                const phoneKey = normalizePhoneForCustomerDocId(order.phone);
                const customerSms = phoneKey ? customerSmsByPhone[phoneKey] : undefined;
                let optInLabel = 'Not opted in';
                let optInClass = 'not-opted-in';
                if (!phoneKey) {
                  optInLabel = 'No phone on order';
                } else if (pushNotificationSmsLookupLoading && customerSms === undefined) {
                  optInLabel = 'Checking subscription…';
                  optInClass = 'checking';
                } else if (customerSms && customerSms.smsSubscribed === true && !customerSms.optedOut) {
                  optInLabel = 'Opted in';
                  optInClass = 'opted-in';
                }
                return (
                <label key={order.id} className="push-notification-item">
                  <input
                    type="checkbox"
                    checked={pushNotificationSelectedIds.has(order.id)}
                    onChange={(e) => {
                      setPushNotificationSelectedIds(prev => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(order.id); else next.delete(order.id);
                        return next;
                      });
                    }}
                  />
                  <span className="push-notification-item-label">Order: {order.id}</span>
                  {order.receiptNumber && <span className="push-notification-item-receipt">Receipt: {order.receiptNumber}</span>}
                  {order.name && <span className="push-notification-item-name">{order.name}</span>}
                  <span className={`push-notification-item-optin ${optInClass}`}>
                    {optInLabel}
                  </span>
                </label>
              ); })
            }
            </div>
            {pushNotificationResult && (
              <div className={`push-notification-result ${pushNotificationResult.failed > 0 ? 'has-errors' : 'success'}`}>
                {(pushNotificationResult.sent > 0 || pushNotificationResult.failed > 0) && (
                  <p>Notifications — Sent: {pushNotificationResult.sent || 0}, Failed: {pushNotificationResult.failed || 0}</p>
                )}
                {pushNotificationResult.markedAsPickedUp ? (
                  <p>{pushNotificationResult.markedAsPickedUp} order{pushNotificationResult.markedAsPickedUp !== 1 ? 's' : ''} marked as picked up.</p>
                ) : null}
                {pushNotificationResult.errors && pushNotificationResult.errors.length > 0 && (
                  <ul className="push-notification-errors">
                    {pushNotificationResult.errors.slice(0, 10).map((err, i) => <li key={i}>{err}</li>)}
                    {pushNotificationResult.errors.length > 10 && <li>… and {pushNotificationResult.errors.length - 10} more</li>}
                  </ul>
                )}
              </div>
            )}
            </div>
            <div className="modal-footer push-notification-footer">
              <button type="button" className="modal-btn" onClick={() => {
                if (pushModalBusy) return;
                setShowPushNotificationCombinedConfirm(false);
                setShowPushNotificationModal(false);
              }}>Cancel</button>
              <button
                type="button"
                className="modal-btn mark-picked-up-btn"
                disabled={pushNotificationSelectedIds.size === 0 || pushModalBusy}
                onClick={markSelectedOrdersAsPickedUp}
              >
                {markAsPickedUpSending ? 'Marking...' : `Mark as picked up (${pushNotificationSelectedIds.size})`}
              </button>
              <button
                type="button"
                className="modal-btn save-btn"
                disabled={pushNotificationSelectedIds.size === 0 || pushModalBusy}
                onClick={async () => {
                  if (pushNotificationSelectedIds.size === 0) return;
                  setPushNotificationSending(true);
                  setPushNotificationResult(null);
                  try {
                    const resend = httpsCallable(functions, 'resendReadyForPickUpNotification2');
                    const { data } = await resend({ orderIds: Array.from(pushNotificationSelectedIds) });
                    setPushNotificationResult(data);
                    if (data.sent > 0 && data.failed === 0) setPushNotificationSelectedIds(new Set());
                  } catch (err) {
                    setPushNotificationResult({ sent: 0, failed: pushNotificationSelectedIds.size, errors: [err.message || String(err)] });
                  } finally {
                    setPushNotificationSending(false);
                  }
                }}
              >
                {pushNotificationSending ? 'Sending...' : `Resend notification (${pushNotificationSelectedIds.size})`}
              </button>
              <button
                type="button"
                className="modal-btn combined-action-btn"
                disabled={combinedPickupOrders.length === 0 || pushModalBusy}
                onClick={() => {
                  setPushNotificationResult(null);
                  setShowPushNotificationCombinedConfirm(true);
                }}
              >
                {combinedPushActionSending ? 'Working...' : 'Send & Complete'}
              </button>
            </div>
            {showPushNotificationCombinedConfirm && (
              <div className="push-notification-confirm-overlay">
                <div className="push-notification-confirm-card" role="dialog" aria-labelledby="push-combined-confirm-title">
                  <h3 id="push-combined-confirm-title">Confirm combined action</h3>
                  <p className="push-notification-confirm-intro">Please review before continuing:</p>
                  <div className="push-notification-confirm-section notify">
                    <h4>Sending notifications to {combinedNotifyOrders.length} order{combinedNotifyOrders.length !== 1 ? 's' : ''}</h4>
                    {combinedNotifyOrders.length === 0 ? (
                      <p className="push-notification-confirm-empty">No orders selected — no notifications will be sent.</p>
                    ) : (
                      <ul>
                        {combinedNotifyOrders.map((order) => (
                          <li key={order.id}>{formatPushNotificationOrderLabel(order)}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="push-notification-confirm-section pickup">
                    <h4>Moving {combinedPickupOrders.length} order{combinedPickupOrders.length !== 1 ? 's' : ''} to picked up</h4>
                    {combinedPickupOrders.length === 0 ? (
                      <p className="push-notification-confirm-empty">No unselected orders — nothing will be marked picked up.</p>
                    ) : (
                      <ul>
                        {combinedPickupOrders.map((order) => (
                          <li key={order.id}>{formatPushNotificationOrderLabel(order)}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="push-notification-confirm-actions">
                    <button
                      type="button"
                      className="modal-btn"
                      disabled={combinedPushActionSending}
                      onClick={() => setShowPushNotificationCombinedConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="modal-btn combined-action-btn"
                      disabled={combinedPushActionSending || (combinedNotifyOrders.length === 0 && combinedPickupOrders.length === 0)}
                      onClick={() => runCombinedPushAndPickup(
                        combinedNotifyOrders.map((o) => o.id),
                        combinedPickupOrders.map((o) => o.id)
                      )}
                    >
                      {combinedPushActionSending ? 'Working...' : 'Complete'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    <audio id="orderSound" src="../../chime.wav" preload="auto"></audio>
    </div>
  );
}

export default UserDashboard;
